import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Select, MenuItem, FormControl, InputLabel, Chip, IconButton, Divider, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Menu, Tooltip, Zoom, CircularProgress, Skeleton,
  Grid, InputAdornment, List, ListItem, ListItemText, ListItemIcon, Switch, FormControlLabel
} from '@mui/material';
import {
  Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Delete as DeleteIcon, Add as AddIcon,
  ArrowBack as ArrowBackIcon, Close as CloseIcon, MoreVert as MoreVertIcon, Lock as LockIcon,
  LockOpen as LockOpenIcon, History as HistoryIcon, Restore as RestoreIcon, Warning as WarningIcon,
  AttachFile as AttachFileIcon, CloudUpload as CloudUploadIcon, Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon, FilterList as FilterListIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import axios from 'axios';
import { campaignApi, accrualApi, referenceApi, api, versionApi, dimensionApi } from '../services/api';
import lockUnlockApi from '../services/lockUnlockApi';
import { 
  formatCurrency, 
  parseCurrency, 
  calculateUnused,
  generateMonthsRange,
  determineCampaignStatus,
  calculateSaving, 
  calculateCheck,
  calculateActualGrossCost,
  calculateSavingPercentage
} from '../utils/calculations';
import HistoryDialog from '../components/HistoryDialog';
import usePermissions from '../hooks/usePermissions';
import './ExcelLikeViewCustom.css'; // Import custom CSS

// Create a completely isolated wrapper to override all global styles
const ExcelViewWrapper = ({ children }) => {
  useEffect(() => {
    // Create a style element to support table-box scrolling
    const styleElement = document.createElement('style');
    styleElement.id = 'excel-view-override-styles';
    styleElement.innerHTML = `
      /* CRITICAL: Support table-box container */
      .excel-view-isolated {
        overflow-x: hidden !important;
        overflow-y: visible !important;
        max-width: 100% !important;
        width: 100% !important;
        position: relative !important;
      }
      
      /* Override any container constraints within excel view */
      .excel-view-isolated .MuiContainer-root,
      .excel-view-isolated .MuiBox-root {
        max-width: none !important;
        width: auto !important;
        overflow-x: visible !important;
      }
      
      /* Support table-box scrolling */
      .excel-view-isolated .table-box {
        overflow: hidden !important;
        position: relative !important;
      }
      
      .excel-view-isolated .table-box .MuiTableContainer-root {
        overflow-x: auto !important;
        overflow-y: auto !important;
        max-width: 100% !important;
        width: 100% !important;
        height: 100% !important;
      }
      
      /* Force table to expand to trigger scroll */
      .excel-view-isolated .table-box .MuiTable-root {
        width: auto !important;
        min-width: max-content !important;
        max-width: none !important;
        table-layout: auto !important;
      }
      
      /* Allow cells to size naturally but enable resize */
      .excel-view-isolated .table-box .MuiTableCell-root {
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        position: relative !important;
      }
    `;
    
    document.head.appendChild(styleElement);
    
    return () => {
      const existingStyle = document.getElementById('excel-view-override-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);
  
  return (
    <div className="excel-view-isolated" style={{
      width: '100%',
      overflowX: 'hidden',
      overflowY: 'visible',
      maxWidth: '100%',
      position: 'relative'
    }}>
      {children}
    </div>
  );
};

// Define consistent font sizes (matching Campaigns component)
const FONT_SIZES = {
  header: '0.9rem',
  subtitle: '0.75rem', 
  body: '0.75rem',
  caption: '0.7rem',
  small: '0.65rem',
  table: '0.7rem',
  tableHeader: '0.7rem',
  number: '0.7rem'
};

// Display component for cell content based on column type
// Used in edit mode to display formatted values
export const RowCellDisplay = ({ column, value }) => {
  if (value === null || value === undefined) {
    return <span>-</span>;
  }
  
  // Format based on column type
  switch (column.type) {
    case 'currency':
      return <span>{formatCurrency(value)}</span>;
    case 'percentage':
      return <span>{value}%</span>;
    case 'date':
      try {
        return <span>{format(new Date(value), 'MMM yyyy')}</span>;
      } catch (e) {
        return <span>{value}</span>;
      }
    case 'status':
      return (
        <Chip
          size="small"
          label={value}
          color={value === 'planning' ? 'primary' : value === 'actual' ? 'success' : 'default'}
          variant="outlined"
          sx={{ borderRadius: '4px', minWidth: '80px', textAlign: 'center' }}
        />
      );
    default:
      return <span>{value}</span>;
  }
};

// Edit field component based on column type
const EditFormField = ({ column, value, onChange, fieldName, vendors, digitalAdFormats, oohFormats, campaign, dimensionData, disabled = false }) => {
  const [inputValue, setInputValue] = useState(''); 
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (column.type === 'currency') {
      if (!isFocused) {
        setInputValue(value !== null && value !== undefined && value !== 0 ? formatCurrency(value) : (value === 0 ? '0,00' : ''));
      }
      // If focused, inputValue is controlled by user typing.
    } else {
      setInputValue(value || ''); 
    }
  }, [value, column.type, isFocused]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value); 
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (column.type === 'currency') {
      const numericValue = parseCurrency(inputValue);
      onChange(numericValue); 
    } else {
      onChange(inputValue); 
    }
  };
  
  const handleFocus = () => {
    setIsFocused(true);
    if (column.type === 'currency') {
      if (value === 0 || value === null || value === undefined) {
        setInputValue('');
      } else {
        setInputValue(String(value).replace('.', ',')); 
      }
    }
  };

  const genericHandleChange = (e) => { onChange(e.target.value); };
  const handleDateChange = (date) => { onChange(date); };
  const handleDropdownChange = (event) => { onChange(event.target.value); };

  // Helper function to get dropdown options based on field name
  const getDropdownOptions = (fieldName) => {
    switch (fieldName) {
      case 'budget_format':
        return [
          { value: 'AP', label: 'AP' },
          { value: 'Top-up', label: 'Top-up' },
        ];
      case 'digital_vendor':
        // Use dimension data first, then fallback to legacy data
        if (dimensionData.digitalVendors && dimensionData.digitalVendors.length > 0) {
          return dimensionData.digitalVendors;
        }
        return (vendors || []).map(vendor => ({
          value: vendor.digital_vendor_name || vendor.name,
          label: vendor.digital_vendor_name || vendor.name
        }));
      case 'digital_ad_format':
        // Use dimension data first, then fallback to legacy data
        if (dimensionData.digitalAdFormats && dimensionData.digitalAdFormats.length > 0) {
          return dimensionData.digitalAdFormats;
        }
        return (digitalAdFormats || []).map(format => ({
          value: format.digital_ad_format_name || format.name,
          label: format.digital_ad_format_name || format.name
        }));
      case 'ooh_format':
        // Use dimension data first, then fallback to legacy data
        if (dimensionData.oohFormats && dimensionData.oohFormats.length > 0) {
          return dimensionData.oohFormats;
        }
        return (oohFormats || []).map(format => ({
          value: format.ooh_format_name || format.name,
          label: format.ooh_format_name || format.name
        }));
      case 'sub_media_type':
        // Use dimension data first, then fallback to hardcoded list
        if (dimensionData.subMediaTypes && dimensionData.subMediaTypes.length > 0) {
          return dimensionData.subMediaTypes;
        }
        return [
          { value: 'Digital Social', label: 'Digital Social' },
          { value: 'Digital Display', label: 'Digital Display' },
          { value: 'Digital Video', label: 'Digital Video' },
          { value: 'Digital Search', label: 'Digital Search' },
          { value: 'Digital Audio', label: 'Digital Audio' },
          { value: 'Digital OTT', label: 'Digital OTT' },
          { value: 'OOH (Shorterm)', label: 'OOH (Shorterm)' },
          { value: 'OOH (Longterm)', label: 'OOH (Longterm)' },
          { value: 'TV Rated', label: 'TV Rated' },
          { value: 'TV Online', label: 'TV Online' },
          { value: 'Radio', label: 'Radio' },
          { value: 'Print', label: 'Print' },
          { value: 'Other', label: 'Other' }
        ];
      case 'monthly_status':
        return [
          { value: 'planning', label: 'Planning' },
          { value: 'actual', label: 'Actual' }
        ];
      case 'region_focus':
        return [
          { value: 'National', label: 'National' },
          { value: 'HCMC', label: 'HCMC' },
          { value: 'Central', label: 'Central' },
          { value: 'North', label: 'North' },
          { value: 'Mekong', label: 'Mekong' }
        ];
      case 'month':
        // Priority 1: Use database dimension data
        if (dimensionData.months && dimensionData.months.length > 0) {
          console.log('✅ USING DATABASE MONTHS! Count:', dimensionData.months.length);
          console.log('📅 Available months range:', 
            dimensionData.months[0]?.label, 
            'to', 
            dimensionData.months[dimensionData.months.length - 1]?.label
          );
          
          // If we have campaign date range, filter months within that range
          if (campaign && campaign.start_date && campaign.end_date) {
            const startDate = new Date(campaign.start_date);
            const endDate = new Date(campaign.end_date);
            
            const filteredMonths = dimensionData.months.filter(month => {
              const monthDate = new Date(month.full_date);
              return monthDate >= startDate && monthDate <= endDate;
            });
            
            console.log('🎯 Filtered months for campaign range:', filteredMonths.length);
            return filteredMonths;
          }
          
          // Otherwise return all available months
          return dimensionData.months;
        }
        
        // Priority 2: Generate based on campaign date range (HARDCODE FALLBACK)
        console.warn('⚠️ FALLBACK: Using hardcoded months based on campaign dates');
        if (campaign && campaign.start_date && campaign.end_date) {
          const months = generateMonthsRange(campaign.start_date, campaign.end_date);
          return months.map(month => ({
            value: month,
            label: format(new Date(month), 'MMM yyyy')
          }));
        }
        
        // Priority 3: Final fallback to current year months (HARDCODE FALLBACK)
        console.warn('⚠️ FINAL FALLBACK: Using hardcoded current year months');
        const currentYear = new Date().getFullYear();
        const fallbackMonths = [];
        for (let i = 0; i < 12; i++) {
          const date = new Date(currentYear, i, 1);
          fallbackMonths.push({
            value: date.toISOString().slice(0, 7) + '-01',
            label: format(date, 'MMM yyyy')
          });
        }
        return fallbackMonths;
      case 'asset_video_length_image_type':
        // Use dimension data first, then fallback to hardcoded list
        if (dimensionData.assetTypes && dimensionData.assetTypes.length > 0) {
          return dimensionData.assetTypes;
        }
        return [
          { value: '15s', label: '15 seconds' },
          { value: '30s', label: '30 seconds' },
          { value: '45s', label: '45 seconds' },
          { value: '60s', label: '60 seconds' },
          { value: '90s', label: '90 seconds' },
          { value: '120s', label: '2 minutes' },
          { value: 'Static Image', label: 'Static Image' },
          { value: 'Animated GIF', label: 'Animated GIF' },
          { value: 'Carousel', label: 'Carousel' },
          { value: 'Video Banner', label: 'Video Banner' },
          { value: 'Rich Media', label: 'Rich Media' },
          { value: 'Interactive', label: 'Interactive' },
          { value: 'HTML5', label: 'HTML5' },
          { value: 'Other', label: 'Other' }
        ];
      default:
        return [];
    }
  };

  // Check if field should be dropdown
  const shouldUseDropdown = (fieldName) => {
    return ['digital_vendor', 'digital_ad_format', 'ooh_format', 'sub_media_type', 'monthly_status', 'region_focus', 'month', 'asset_video_length_image_type', 'budget_format'].includes(fieldName);
  };

  // If field should use dropdown, render Select component
  if (shouldUseDropdown(fieldName)) {
    const options = getDropdownOptions(fieldName);
    
    // Debug log for month dropdown (only when debugging)
    if (fieldName === 'month' && process.env.NODE_ENV === 'development' && window.debugMode) {
      console.log('🎯 MONTH DROPDOWN RENDERING:', {
        fieldName,
        optionsLength: options?.length || 0,
        currentValue: value
      });
    }
    
    // Check if current value exists in options
    const valueExists = options.some(option => option.value === value) || value === '' || value === null || value === undefined;
    const selectValue = valueExists ? (value || '') : '';
    
    return (
      <Select
        size="small"
        variant="outlined"
        value={selectValue}
        onChange={handleDropdownChange}
        disabled={disabled}
        sx={{ 
          width: '100%',
          fontSize: '0.7rem',
          height: '32px',
          backgroundColor: disabled ? '#f5f5f5' : 'white',
          borderRadius: 1,
          '& .MuiSelect-select': {
            fontSize: '0.7rem',
            py: 0.5,
            px: 0.75
          },
          '&:hover': {
            backgroundColor: disabled ? '#f5f5f5' : '#f8f9fa'
          },
          '&.Mui-focused': {
            backgroundColor: 'white',
            boxShadow: '0 0 0 2px rgba(76, 175, 80, 0.2)'
          },
          '&.Mui-disabled': {
            backgroundColor: '#f5f5f5',
            color: '#999'
          }
        }}
      >
        <MenuItem value="">
          <em>-- Chọn --</em>
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
        {/* Show current value as option if it doesn't exist in the list */}
        {!valueExists && value && (
          <MenuItem value={value} disabled>
            <em>{value} (không có trong danh sách)</em>
          </MenuItem>
        )}
      </Select>
    );
  }

  switch (column.type) {
    case 'currency':
      return (
        <TextField
          type="text"
          inputMode="decimal"
          size="small"
          variant="outlined"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          inputProps={{ style: { textAlign: 'right', fontSize: '0.7rem' } }}
          sx={{ 
            width: '100%',
            '& .MuiOutlinedInput-root': {
              fontSize: '0.7rem',
              height: '32px',
              backgroundColor: disabled ? '#f5f5f5' : 'white',
              borderRadius: 1,
              '& input': {
                fontSize: '0.7rem',
                py: 0.5,
                px: 0.75
              },
              '&:hover': {
                backgroundColor: disabled ? '#f5f5f5' : '#f8f9fa'
              },
              '&.Mui-focused': {
                backgroundColor: 'white',
                boxShadow: '0 0 0 2px rgba(76, 175, 80, 0.2)'
              },
              '&.Mui-disabled': {
                backgroundColor: '#f5f5f5',
                color: '#999'
              }
            }
          }}
        />
      );
    case 'percentage':
      return (
        <TextField
          type="number"
          size="small"
          variant="outlined"
          value={value || ''} 
          onChange={genericHandleChange}
          inputProps={{
            step: 0.1,
            min: 0,
            max: 100,
            style: { textAlign: 'right', fontSize: '0.8rem' },
          }}
          sx={{ 
            width: '100%',
            '& .MuiOutlinedInput-root': {
              fontSize: '0.8rem',
              height: '36px',
              '& input': {
                fontSize: '0.8rem',
                py: 0.5,
                px: 0.6
              }
            }
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end" sx={{ fontSize: '0.8rem' }}>%</InputAdornment>
          }}
        />
      );
      
    case 'date':
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            views={['year', 'month']}
            value={value ? new Date(value) : null}
            onChange={handleDateChange}
            renderInput={(params) => (
              <TextField 
                {...params}
                size="small"
                sx={{ width: '100%' }}
              />
            )}
          />
        </LocalizationProvider>
      );
      
    case 'status':
      return (
        <Select
          size="small"
          variant="outlined"
          value={value || ''}
          onChange={handleDropdownChange}
          sx={{ 
            width: '100%',
            fontSize: '0.8rem',
            height: '36px',
            '& .MuiSelect-select': {
              fontSize: '0.8rem',
              py: 0.5,
              px: 0.6
            }
          }}
        >
          <MenuItem value="planning">Planning</MenuItem>
          <MenuItem value="actual">Actual</MenuItem>
          <MenuItem value="expired">Expired</MenuItem>
        </Select>
      );
      
    default:
      return (
        <TextField
          type="text"
          size="small"
          variant="outlined"
          value={value || ''} 
          onChange={genericHandleChange}
          disabled={disabled}
          sx={{ 
            width: '100%',
            '& .MuiOutlinedInput-root': {
              fontSize: '0.7rem',
              height: '32px',
              backgroundColor: disabled ? '#f5f5f5' : 'white',
              borderRadius: 1,
              '& input': {
                fontSize: '0.7rem',
                py: 0.5,
                px: 0.75
              },
              '&:hover': {
                backgroundColor: disabled ? '#f5f5f5' : '#f8f9fa'
              },
              '&.Mui-focused': {
                backgroundColor: 'white',
                boxShadow: '0 0 0 2px rgba(76, 175, 80, 0.2)'
              },
              '&.Mui-disabled': {
                backgroundColor: '#f5f5f5',
                color: '#999'
              }
            }
          }}
        />
      );
  }
};

/**
 * ExcelLikeView Component
 * 
 * This component provides an Excel-like interface for viewing and editing campaign accrual data.
 * It supports both live data and historical snapshot data.
 * 
 * URL Structure:
 * - /excel-view/{campaignId}/{mediaType} - Live data
 * - /excel-view/{campaignId}/{mediaType}?snapshotMonth=YYYY-MM - Snapshot data
 * 
 * Key Features:
 * - Excel-like table with month columns and cost breakdown rows
 * - Support for different media types (Digital, OOH, TV, Radio)
 * - Read-only snapshot mode vs editable live mode
 * - Vendor and format dropdowns for Digital/OOH media types
 * - Row-level locking and version history
 * - Bulk operations and data validation
 * 
 * Data Flow:
 * 1. Extract campaignId, mediaType from URL params
 * 2. Check for snapshotMonth query param to determine mode
 * 3. Fetch campaign info and accrual data (live or snapshot)
 * 4. Load reference data (vendors, formats) based on media type
 * 5. Render Excel-like table with appropriate columns and controls
 */
const ExcelLikeView = () => {
  // URL Parameters and Navigation
  const { campaignId, mediaType } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const snapshotMonth = params.get('snapshotMonth'); // If present, load snapshot data
  const navigate = useNavigate();
  
  // Permission checking
  const { hasPermission } = usePermissions();
  const canEditAccruals = hasPermission('edit_accruals');
  
  // Function to check if a specific field can be edited
  const canEditField = (fieldName) => {
    // If user doesn't have general edit_accruals permission, they can't edit anything
    if (!canEditAccruals) return false;
    
    // All fields are editable if user has edit_accruals permission
    // Note: actual_gross_cost is calculated field and should not be editable
    if (fieldName === 'actual_gross_cost') return false;
    
    return true;
  };
  
  // Core Data States
  const [campaign, setCampaign] = useState(null); // Campaign basic info
  const [accrualData, setAccrualData] = useState([]);
  const [vendors, setVendors] = useState([]); // For Digital media type dropdowns
  const [digitalAdFormats, setDigitalAdFormats] = useState([]); // For Digital media type dropdowns
  const [oohFormats, setOohFormats] = useState([]); // For OOH media type dropdowns
  
  // Dimension Data States - for dropdown options
  const [dimensionData, setDimensionData] = useState({
    months: [],
    assetTypes: [],
    subMediaTypes: [],
    digitalVendors: [],
    digitalAdFormats: [],
    oohFormats: []
  });
  
  const [loading, setLoading] = useState(true); // Loading state

  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Show snackbar message
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Handle closing snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const [monthRange, setMonthRange] = useState([]);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [campaignMediaTypeId, setCampaignMediaTypeId] = useState(null);
  const [isPreview, setIsPreview] = useState(true);
  const [previewSnapshot, setPreviewSnapshot] = useState([]);
  const [isSnapshot, setIsSnapshot] = useState(false);
  // 🔧 FIX: Improved state management for edit mode
  const [editState, setEditState] = useState({
    isEditing: false,
    originalData: [],
    editedData: [],
    pendingChanges: new Set(),
    deletedIds: [], // Track explicitly deleted rows
    hasUnsavedChanges: false
  });
  const [campaignData, setCampaignData] = useState(null);
  const [originalAccrualData, setOriginalAccrualData] = useState([]);
  
  // Filter and Sort States
  const [filterValues, setFilterValues] = useState({});
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [filteredData, setFilteredData] = useState([]);

  // Legacy compatibility
  const isEditMode = editState.isEditing;
  const editedData = editState.editedData;
  const setIsEditMode = (value) => {
    setEditState(prev => ({ ...prev, isEditing: value }));
  };
  const setEditedData = (data) => {
    setEditState(prev => ({ ...prev, editedData: data }));
  };

  // Determine if we're viewing a snapshot
  const isSnapshotView = useMemo(() => Boolean(snapshotMonth), [snapshotMonth]);

  // Add state for campaign selector
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [showCampaignSelector, setShowCampaignSelector] = useState(false);
  const [rowUnlockStatus, setRowUnlockStatus] = useState({});
  const [rowHistory, setRowHistory] = useState([]);
  const [historyRowId, setHistoryRowId] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  // Unlock request dialog state
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [unlockRowId, setUnlockRowId] = useState(null);
  const [unlockReason, setUnlockReason] = useState('');
  const [unlockAttachment, setUnlockAttachment] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');

  // Column resize functionality - Restored original widths for resize functionality
  const [columnWidths, setColumnWidths] = useState({
    edit: 40,
    data: 60,
    month: 80,
    status: 100,
    media: 80,
    sub_media: 120, // New sub_media_type column
    vendor: 100,
    format: 100,
    region: 100,
    budget_format: 130,
    ooh: 80,
    asset: 120,
    given: 100,
    planning: 120,
    actual: 120,
    net_cost: 100,
    tracking: 100,
    tax: 80,
    ooscope: 100,
    saving: 120,
    po: 100,
    unused: 100,
    coupon: 100,
    ntw: 100,
    north: 100,
    central: 100,
    rest: 100,
    ghcm: 100,
    mekong: 100,
    check: 100
  });
  
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  // Data menu state
  const [dataMenuAnchor, setDataMenuAnchor] = useState(null);
  const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);

  // State for file upload loading
  const [uploadingFile, setUploadingFile] = useState(false);

  // Load data on component mount
  useEffect(() => {
    // Log mediaType for debugging
    console.log('Current mediaType:', mediaType);
    console.log('mediaType === "Digital":', mediaType === 'Digital');
    console.log('mediaType === "Digital Amplify":', mediaType === 'Digital Amplify');
    console.log('mediaType.toLowerCase() === "digital amplify":', mediaType && mediaType.toLowerCase() === 'digital amplify');
    
    if (campaignId) {
      setLoading(true);
      // Check if we're viewing snapshot data first
      if (snapshotMonth) {
        // For snapshot data, use snapshot-specific logic
        fetchSnapshotData();
      } else {
        // For live data, use regular logic
        fetchCampaignData();
      }
      fetchReferenceData();
      // Note: fetchCampaignMediaTypeId and fetchVersions are called in their own useEffect hooks below
    } else {
      // When no campaign is selected, show list of campaigns instead
      setLoading(false);
    }
    
    // Log page view
    const logPageView = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (token) {
          // Use window.AMS_CONFIG for production compatibility
          const BACKEND_API_URL = window.AMS_CONFIG?.API_URL || 'http://localhost:8000';
          const API_PATH = window.AMS_CONFIG?.API_PATH || '/api';
          const API_BASE_URL = `${BACKEND_API_URL}${API_PATH}`;
          const endpoints = [
            `${API_BASE_URL}/log-page-view/`
          ];
          
          for (const endpoint of endpoints) {
            try {
              await axios.post(
                endpoint,
                { page_name: 'ExcelLikeView' },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              console.log(`Page view logged successfully at ${endpoint}`);
              return; // Exit loop on success
            } catch (err) {
              console.error(`Error logging page view at ${endpoint}:`, err);
            }
          }
          console.error('Failed to log page view at any endpoint');
        }
      } catch (error) {
        console.error('Error logging page view:', error);
      }
    };

    logPageView();
  }, [campaignId, mediaType, snapshotMonth]);

  // Update document title when campaign data is loaded
  useEffect(() => {
    if (campaign) {
      const campaignName = campaign.campaign_name || campaign.name || 'Campaign';
      const brandName = campaign.brand_name || campaign.brand || '';
      const titleParts = ['excel-view'];
      
      if (brandName) {
        titleParts.push(brandName);
      }
      
      titleParts.push(campaignName);
      titleParts.push(mediaType);
      
      document.title = titleParts.join(' / ');
      
      // Also update window title for better UX
      if (window.history && window.history.replaceState) {
        const newUrl = window.location.pathname + window.location.search;
        window.history.replaceState(
          { ...window.history.state, title: document.title }, 
          document.title, 
          newUrl
        );
      }
    }
    
    // Cleanup: reset title when component unmounts
    return () => {
      document.title = 'AMS - Accrual Management System';
    };
  }, [campaign, mediaType]);

  // Process and normalize accrual data for display
  const processAccrualData = (data) => {
    if (!Array.isArray(data)) return [];
    
    console.log('processAccrualData called with:', {
      dataLength: data.length,
      campaign: campaign ? {
        id: campaign.id,
        name: campaign.name,
        start_date: campaign.start_date,
        end_date: campaign.end_date
      } : 'null'
    });
    
    return processAccrualDataWithCampaign(data, campaign);
  };

  // Process and normalize accrual data for display with explicit campaign data
  const processAccrualDataWithCampaign = (data, campaignData) => {
    if (!Array.isArray(data)) return [];
    
    console.log('processAccrualDataWithCampaign called with:', {
      dataLength: data.length,
      campaign: campaignData ? {
        id: campaignData.id,
        name: campaignData.name,
        start_date: campaignData.start_date,
        end_date: campaignData.end_date
      } : 'null'
    });

    // Removed n-2 auto-update: monthly_status now driven only by presence of actual inputs
    
    const currencyFields = [
      'given', 'planning_gross_cost', 'net_net_cost', 
      'whtax', 'ooscope_fees', 'tracking_fees', 
      'cost_on_coupon_voucher_etc', 'po_cost', 'cost_on_ecpms',
      'ntw', 'north', 'central_mid', 'the_rest_of_central', 'ghcm', 'mekong', 'coupon'
    ];

    return data.map(row => {
      const newRow = { ...row };
      // Normalize backend statuses to UI statuses
      if (newRow.monthly_status === 'locked' || newRow.monthly_status === 'sealed') {
        newRow.monthly_status = 'actual';
      }
      currencyFields.forEach(field => {
        if (newRow.hasOwnProperty(field)) {
          if (typeof newRow[field] === 'string') {
            newRow[field] = parseFloat(newRow[field].replace(/,/g, '')) || 0;
          } else if (typeof newRow[field] !== 'number') {
            newRow[field] = 0;
          }
        }
      });
      
      // Calculate actual_gross_cost automatically from components (not editable)
      newRow.actual_gross_cost = calculateActualGrossCost(newRow);
      
      // Calculate unused
      newRow.unused = calculateUnused(newRow);
      
      // 🔧 UPDATED: Auto-update monthly_status to "actual" when actual data exists
      // Logic: If any component costs > 0, automatically change status from "planning" to "actual"
      // But only if monthly_status hasn't been manually set
      const hasActualData = (
        (typeof newRow.net_net_cost === 'number' && newRow.net_net_cost > 0) ||
        (typeof newRow.whtax === 'number' && newRow.whtax > 0) ||
        (typeof newRow.ooscope_fees === 'number' && newRow.ooscope_fees > 0) ||
        (typeof newRow.tracking_fees === 'number' && newRow.tracking_fees > 0) ||
        (typeof newRow.cost_on_coupon_voucher_etc === 'number' && newRow.cost_on_coupon_voucher_etc > 0) ||
        (typeof newRow.actual_gross_cost === 'number' && newRow.actual_gross_cost > 0)
      );
      
      if (hasActualData && (!newRow.monthly_status || newRow.monthly_status === 'planning')) {
        newRow.monthly_status = 'actual';
        console.log(`🔄 Auto-updated monthly_status to "actual" for row ${newRow.id || 'new'} - actual data detected`);
      } else if (hasActualData && newRow.monthly_status === 'actual') {
        console.log(`✅ Monthly status already "actual" for row ${newRow.id || 'new'} (manual or auto)`);
      }
      
      // 🔧 UPDATED: Only calculate/display saving when monthly_status = 'actual'
      if (newRow.monthly_status === 'actual') {
        if (newRow.provisional_saving !== undefined && newRow.provisional_saving !== null) {
          newRow.saving = parseFloat(newRow.provisional_saving) || 0;
          newRow.savingDisplay = undefined;
        } else {
          const planningNum = Number(newRow.planning_gross_cost) || 0;
          const actualNum = Number(newRow.actual_gross_cost) || 0;
          newRow.saving = planningNum - actualNum;
          newRow.savingDisplay = undefined;
        }
        newRow.savingPercentage = calculateSavingPercentage(newRow, campaignData, newRow.actual_gross_cost);
      } else {
        newRow.saving = 0;
        newRow.savingDisplay = 'NA';
        newRow.savingPercentage = { percentage: 0, display: 'NA', style: {} };
      }
      
      // Calculate check value with proper styling
      newRow.check = calculateCheck(newRow);
      
      return newRow;
    });
  };

  /**
   * Fetch snapshot data function - Loads campaign and accrual data from historical snapshot
   * 
   * This function handles loading data from a specific month's snapshot instead of live data.
   * Snapshots contain historical campaign states with media types and monthly accrual data.
   * 
   * Flow:
   * 1. Get all campaigns from snapshot month via API
   * 2. Find the specific campaign by ID in snapshot data
   * 3. Set campaign basic info (name, dates, etc.)
   * 4. Fetch detailed accrual data for the campaign's media type
   * 5. Process and display the snapshot data in Excel-like format
   * 
   * Note: Snapshot data is read-only - no editing capabilities
   */
  const fetchSnapshotData = async () => {
    console.log('fetchSnapshotData called with:', { campaignId, mediaType, snapshotMonth });
    setLoading(true);
    
    try {
      // Step 1: Get snapshot campaign info to validate campaign exists
      // API: GET /api/campaigns-data/?snapshot_month=YYYY-MM
      // Returns: Array of campaigns from that month's snapshot
      const campaignRes = await campaignApi.getCampaignsBySnapshot(snapshotMonth);
      console.log('Campaign Snapshot API Response:', campaignRes.data);
      
      // Step 2: Find current campaign in snapshot data
      // Handle different response formats from the API
      let campaignData = null;
      
      if (campaignRes.data.results) {
        // Paginated response format: { results: [...], count: N }
        campaignData = campaignRes.data.results.find(
          item => item.id.toString() === campaignId.toString()
        );
      } else if (campaignRes.data.snapshot?.campaigns) {
        // Nested snapshot format: { snapshot: { campaigns: [...] } }
        campaignData = campaignRes.data.snapshot.campaigns.find(
          item => item.id.toString() === campaignId.toString()
        );
      } else if (Array.isArray(campaignRes.data)) {
        // Direct array response: [...campaigns...]
        // This is the current format returned by the snapshot API
        campaignData = campaignRes.data.find(
          item => item.id.toString() === campaignId.toString()
        );
      }
      
      if (!campaignData) {
        // Campaign not found in snapshot - redirect to campaign selector
        console.warn('Campaign not found in snapshot, showing available campaigns');
        await checkCampaignAvailability();
        return;
      }
      
      // Step 3: Set campaign basic info from snapshot
      // Normalize field names and ensure required fields are available
      const processedCampaignData = {
        ...campaignData,
        start_date: campaignData.start_date || campaignData.start_date_actual,
        end_date: campaignData.end_date || campaignData.end_date_actual
      };
      setCampaign(processedCampaignData);
      
      // Step 4: Generate month range for Excel columns based on campaign dates
      // This creates the month headers for the Excel-like view
      if (campaignData.start_date && campaignData.end_date) {
        const months = generateMonthsRange(
          campaignData.start_date,
          campaignData.end_date
        );
        setMonthRange(months);
      }
      
      // Step 5: Fetch detailed accrual data for this campaign's media type
      // API: GET /api/campaigns-data/{campaignId}/accrual-detail/?snapshot_month=YYYY-MM&media_type=MediaType
      // This gets the monthly breakdown data for Excel-like view
      console.log('[SNAPSHOT] Using backend API to fetch accrual data');
      const snapshotAccrualResponse = await accrualApi.getSnapshotAccrualData(
        campaignId, 
        snapshotMonth, 
        mediaType
      );
      
      console.log('[SNAPSHOT] Backend accrual data response:', snapshotAccrualResponse.data);
      
      // Step 6: Process the data from backend (already formatted for Excel view)
      // The backend normalizes media type names (Digital_0, Digital_1 -> Digital)
      // and returns data in the format expected by the Excel-like table
      const backendAccrualData = snapshotAccrualResponse.data || [];
      const finalProcessedSnapshotData = processAccrualDataWithCampaign(backendAccrualData, processedCampaignData);
      
      // Step 7: Set the processed data and mark as snapshot mode
      setAccrualData(finalProcessedSnapshotData);
      setIsSnapshot(true); // Enables read-only mode
      setError(null);
      
    } catch (err) {
      console.error('Error fetching snapshot data:', err);
      
      // Better error handling
      if (err.response?.status === 404) {
        setError(`Campaign không tồn tại trong snapshot tháng ${snapshotMonth}.`);
        await checkCampaignAvailability();
      } else {
        setError('Không thể tải dữ liệu snapshot. Vui lòng thử lại sau.');
      }
      
      setAccrualData([]);
      setIsSnapshot(false);
    } finally {
      setLoading(false);
    }
  };

  // Fetch campaign data
  const fetchCampaignData = async () => {
    try {
      console.log('[CURRENT DATA] Fetching campaign and accrual data for:', { campaignId, mediaType });
      
      const campaignResponse = await campaignApi.getCampaign(campaignId);
      const campaignData = campaignResponse.data;
      setCampaign(campaignData);
      
      // Fetch accrual data based on media type
      let accrualResponse;
      if (mediaType === 'Digital') {
        console.log('[CURRENT DATA] Fetching Digital accrual data');
        accrualResponse = await accrualApi.getAccrualDataByMediaType(campaignId, 'Digital');
      } else {
        console.log(`[CURRENT DATA] Fetching ${mediaType} accrual data`);
        accrualResponse = await accrualApi.getAccrualDataByMediaType(campaignId, mediaType);
      }
      
      console.log('[CURRENT DATA] Accrual API response:', accrualResponse);
      
      // Extract data from response (accrualResponse.data is the actual data)
      const accrualData = accrualResponse?.data || [];
      
      if (accrualData && accrualData.length > 0) {
        console.log('[CURRENT DATA] Processing accrual data:', accrualData.length, 'rows');
        // Pass the campaign data directly to ensure it's available during processing
        const processedData = processAccrualDataWithCampaign(accrualData, campaignData);
        
        // Data refreshed successfully
        
        setAccrualData(processedData);
      } else {
        console.log('[CURRENT DATA] No accrual data found');
        setAccrualData([]);
      }
    } catch (error) {
      console.error('Error fetching campaign data:', error);
      
      // Handle 404 errors gracefully - campaign doesn't exist
      if (error.response?.status === 404) {
        showSnackbar(
          `Campaign not found. This campaign may have been deleted or the URL is invalid. Redirecting to campaign list...`,
          'error'
        );
        
        // Redirect to campaign list after 3 seconds
        setTimeout(() => {
          window.location.href = '/campaigns';
        }, 3000);
        return;
      }
      
      const errorMessage = error.response?.data?.error || error.message || 'Error fetching campaign data';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch reference data like vendors, formats, etc.
  const fetchReferenceData = async () => {
    try {
      // Determine the media type
      const mediaTypeLower = mediaType ? mediaType.toLowerCase() : '';
      const isDigitalMediaType = mediaTypeLower === 'digital' || mediaTypeLower === 'digital amplify';
      const isOOHType = mediaTypeLower === 'ooh';
      
      // Array to store all API calls
      const apiCalls = [];
      
      // Add appropriate API calls based on media type
      if (isDigitalMediaType) {
        apiCalls.push(
          referenceApi.getVendors(),
          referenceApi.getDigitalAdFormats()
        );
      } else if (isOOHType) {
        apiCalls.push(referenceApi.getOOHFormats());
      }
      
      // Execute all API calls in parallel
      const results = await Promise.all(apiCalls);
      
      // Process results based on media type
      if (isDigitalMediaType) {
        const [vendorsResponse, formatsResponse] = results;
        
        // Process vendors
        const vendorsData = vendorsResponse?.data?.results || vendorsResponse?.data || [];
        setVendors(Array.isArray(vendorsData) ? vendorsData : []);
        
        // Process digital ad formats
        const formatsData = formatsResponse?.data?.results || formatsResponse?.data || [];
        setDigitalAdFormats(Array.isArray(formatsData) ? formatsData : []);
      } else if (isOOHType) {
        // Process OOH formats
        const [oohFormatsResponse] = results;
        const oohFormatsData = oohFormatsResponse?.data?.results || [];
        // Map the API response to match the expected format {id, name}
        const formattedOohFormats = oohFormatsData.map(item => ({
          id: item.ooh_format_id,
          name: item.ooh_format_name
        }));
        setOohFormats(Array.isArray(formattedOohFormats) ? formattedOohFormats : []);
      }
    } catch (error) {
      console.error('Error fetching reference data:', error);
      setVendors([]);
      setDigitalAdFormats([]);
      setOohFormats([]);
    }
  };

  // Fetch dimension data from database
  const fetchDimensionData = async () => {
    try {
      console.log('🚀 FETCHING DIMENSION DATA FROM DATABASE...');
      
      // Fetch all dimension data in parallel
      const [
        monthsResponse,
        assetTypesResponse,
        subMediaTypesResponse,
        digitalVendorsResponse,
        digitalAdFormatsResponse,
        oohFormatsResponse
      ] = await Promise.allSettled([
        dimensionApi.getMonths(),
        dimensionApi.getAssetTypes(),
        dimensionApi.getSubMediaTypes(),
        dimensionApi.getDigitalVendors(),
        dimensionApi.getDigitalAdFormats(),
        dimensionApi.getOOHFormats()
      ]);
      
      console.log('📋 ALL API RESPONSES RECEIVED:', {
        months: monthsResponse.status,
        assetTypes: assetTypesResponse.status,
        subMediaTypes: subMediaTypesResponse.status,
        digitalVendors: digitalVendorsResponse.status,
        digitalAdFormats: digitalAdFormatsResponse.status,
        oohFormats: oohFormatsResponse.status
      });

      // Process months data - now using custom months endpoint
      let months = [];
      console.log('🔍 Raw months response:', monthsResponse);
      
      if (monthsResponse.status === 'fulfilled' && monthsResponse.value) {
        console.log('🔍 monthsResponse.value:', monthsResponse.value);
        console.log('🔍 monthsResponse.value.data:', monthsResponse.value.data);
        
        // The API returns data directly as an array, not nested in a 'data' property
        const monthsData = monthsResponse.value.data || monthsResponse.value;
        console.log('🔍 monthsData:', monthsData);
        console.log('🔍 Is array?', Array.isArray(monthsData));
        
        if (Array.isArray(monthsData)) {
          months = monthsData.map(item => ({
            value: item.value,
            label: item.label,
            full_date: item.value,
            month: item.month,
            year: item.year,
            month_name: item.month_name
          }));
          
          console.log('✅ Months loaded from DATABASE API:', months.length);
        } else {
          console.error('❌ monthsData is not an array:', typeof monthsData, monthsData);
        }
      } else {
        console.warn('⚠️ Failed to load months from API:', monthsResponse);
        console.warn('⚠️ monthsResponse status:', monthsResponse?.status);
        console.warn('⚠️ monthsResponse value:', monthsResponse?.value);
      }

      // Process asset types data
      let assetTypes = [];
      if (assetTypesResponse.status === 'fulfilled' && assetTypesResponse.value?.data?.results) {
        assetTypes = assetTypesResponse.value.data.results.map(item => ({
          value: item.asset_video_length_image_type_name,
          label: item.asset_video_length_image_type_name
        }));
        console.log('Asset types loaded from dimension:', assetTypes.length);
      }

      // Process sub media types data
      let subMediaTypes = [];
      if (subMediaTypesResponse.status === 'fulfilled' && subMediaTypesResponse.value?.data?.results) {
        subMediaTypes = subMediaTypesResponse.value.data.results.map(item => ({
          value: item.sub_media_type_name,
          label: item.sub_media_type_name
        }));
        console.log('Sub media types loaded from dimension:', subMediaTypes.length);
      }

      // Process digital vendors data
      let digitalVendors = [];
      if (digitalVendorsResponse.status === 'fulfilled' && digitalVendorsResponse.value?.data?.results) {
        digitalVendors = digitalVendorsResponse.value.data.results.map(item => ({
          value: item.digital_vendor_name,
          label: item.digital_vendor_name
        }));
        console.log('Digital vendors loaded from dimension:', digitalVendors.length);
      }

      // Process digital ad formats data
      let digitalAdFormats = [];
      if (digitalAdFormatsResponse.status === 'fulfilled' && digitalAdFormatsResponse.value?.data?.results) {
        digitalAdFormats = digitalAdFormatsResponse.value.data.results.map(item => ({
          value: item.digital_ad_format_name,
          label: item.digital_ad_format_name
        }));
        console.log('Digital ad formats loaded from dimension:', digitalAdFormats.length);
      }

      // Process OOH formats data
      let oohFormats = [];
      if (oohFormatsResponse.status === 'fulfilled' && oohFormatsResponse.value?.data?.results) {
        oohFormats = oohFormatsResponse.value.data.results.map(item => ({
          value: item.ooh_format_name,
          label: item.ooh_format_name
        }));
        console.log('OOH formats loaded from dimension:', oohFormats.length);
      }

      // Update dimension data state
      setDimensionData({
        months,
        assetTypes,
        subMediaTypes,
        digitalVendors,
        digitalAdFormats,
        oohFormats
      });

      console.log('✅ ALL DIMENSION DATA LOADED FROM DATABASE!', {
        months: months.length,
        assetTypes: assetTypes.length,
        subMediaTypes: subMediaTypes.length,
        digitalVendors: digitalVendors.length,
        digitalAdFormats: digitalAdFormats.length,
        oohFormats: oohFormats.length
      });
      
      if (months.length > 0) {
        console.log('🎯 MONTHS DATA FROM DATABASE:', months.slice(0, 3));
      } else {
        console.warn('⚠️ NO MONTHS DATA RECEIVED FROM API!');
      }
      
      // Log the state that will be set
      console.log('📊 FINAL DIMENSION DATA STATE:', {
        months: months.length,
        assetTypes: assetTypes.length,
        subMediaTypes: subMediaTypes.length,
        digitalVendors: digitalVendors.length,
        digitalAdFormats: digitalAdFormats.length,
        oohFormats: oohFormats.length
      });

    } catch (error) {
      console.error('Error fetching dimension data:', error);
      // Keep empty arrays as fallback
      setDimensionData({
        months: [],
        assetTypes: [],
        subMediaTypes: [],
        digitalVendors: [],
        digitalAdFormats: [],
        oohFormats: []
      });
    }
  };

  // Fetch dimension data immediately on component mount (highest priority)
  useEffect(() => {
    console.log('🔄 Component mounted - fetching dimension data first');
    fetchDimensionData();
  }, []); // Run once on mount
  
  // Fetch reference data when component mounts or mediaType changes
  useEffect(() => {
    fetchReferenceData();
  }, [mediaType]);

  // Debug log when dimensionData changes
  useEffect(() => {
    console.log('🔄 DIMENSION DATA STATE CHANGED:', {
      months: dimensionData.months?.length || 0,
      assetTypes: dimensionData.assetTypes?.length || 0,
      subMediaTypes: dimensionData.subMediaTypes?.length || 0,
      digitalVendors: dimensionData.digitalVendors?.length || 0,
      digitalAdFormats: dimensionData.digitalAdFormats?.length || 0,
      oohFormats: dimensionData.oohFormats?.length || 0
    });
    
    if (dimensionData.months?.length > 0) {
      console.log('📅 MONTHS IN STATE:', dimensionData.months.slice(0, 3));
    }
  }, [dimensionData]);

  // Sync editedData with accrualData when needed
  useEffect(() => {
    // Only sync if we're in edit mode and editedData is empty
    // 🔧 FIX: Don't auto-sync if editedData already has new rows (to preserve added rows)
    if (editState.isEditing && accrualData.length > 0) {
      // Only initialize editedData if it's completely empty
      if (editState.editedData.length === 0) {
        console.log('🔄 Initializing editedData with accrualData:', {
          accrualDataLength: accrualData.length,
          editedDataLength: editState.editedData.length,
          isEditing: editState.isEditing
        });
        
        setEditState(prev => ({
          ...prev,
          editedData: accrualData.map(row => ({ ...row }))
        }));
      } else {
        // 🔧 FIX: If editedData has more rows than accrualData, it means we have new rows
        // Don't overwrite them
        console.log('⚠️ EditedData has rows, not syncing to preserve new additions:', {
          accrualDataLength: accrualData.length,
          editedDataLength: editState.editedData.length,
          hasNewRows: editState.editedData.length > accrualData.length
        });
      }
    }
  }, [accrualData, editState.isEditing]);

  // Lấy campaignMediaTypeId khi có campaignId và mediaType (only for live data)
  useEffect(() => {
    const fetchCampaignMediaTypeId = async () => {
      if (!campaignId || !mediaType || snapshotMonth) return; // Skip for snapshot data
      try {
        const res = await campaignApi.getCampaignMediaTypes(campaignId);
        const found = (res.data || []).find(mt => mt.media_type === mediaType);
        if (found) setCampaignMediaTypeId(found.id);
        else setCampaignMediaTypeId(null);
      } catch (err) {
        setCampaignMediaTypeId(null);
      }
    };
    fetchCampaignMediaTypeId();
  }, [campaignId, mediaType, snapshotMonth]);

  // Lấy versions khi có campaignId và campaignMediaTypeId (only for live data)
  useEffect(() => {
    if (!campaignId || snapshotMonth) return; // Skip for snapshot data

    const fetchVersions = async () => {
      try {
        const res = await versionApi.getVersions(campaignId);
        setVersions(res.data.results || []);
      } catch (err) {
        console.error('Error fetching versions:', err);
        setVersions([]);
      }
    };

    fetchVersions();
  }, [campaignId, snapshotMonth]);

  // Hàm restore version
  const handleRestoreVersion = async () => {
    if (!selectedVersion) return;
    try {
      await versionApi.restoreVersion(campaignId, selectedVersion);
      showSnackbar('Restored version successfully');
      // Refresh data after restore
      fetchCampaignData();
      
      // Reload versions after restore
      const response = await versionApi.getVersion(selectedVersion);
      setPreviewSnapshot(response.data.snapshot || []); // Dữ liệu version cũ
      setIsPreview(true);
      setSelectedVersion(selectedVersion);
    } catch (err) {
      console.error('Error restoring version:', err);
      showSnackbar('Failed to restore version', 'error');
    }
  };

  // Khi chọn version trong dropdown
  const handleSelectVersion = async (versionId) => {
    if (versionId === 'current' || !versionId) {
      // Switch back to current/live data
      setSelectedVersion(null);
      setIsPreview(false);
      setPreviewSnapshot([]);
      fetchCampaignData();
    } else {
      // Load specific version for comparison
      setSelectedVersion(versionId);
      try {
        const res = await versionApi.getVersionData(versionId);
        console.log('Version API response:', res.data);
        
        if (res.data && res.data.snapshot) {
          // Ensure snapshot is always an array
          let snapshotData = res.data.snapshot;
          
          // Handle different response formats
          if (Array.isArray(snapshotData)) {
            setPreviewSnapshot(snapshotData);
          } else if (typeof snapshotData === 'object' && snapshotData !== null) {
            // If it's an object with array properties, try to extract the array
            if (snapshotData.accrual_data && Array.isArray(snapshotData.accrual_data)) {
              setPreviewSnapshot(snapshotData.accrual_data);
            } else if (snapshotData.data && Array.isArray(snapshotData.data)) {
              setPreviewSnapshot(snapshotData.data);
            } else {
              // Convert object to single-item array as fallback
              setPreviewSnapshot([snapshotData]);
            }
          } else {
            console.warn('Unexpected snapshot format:', snapshotData);
            setPreviewSnapshot([]);
          }
          
          setIsPreview(true);
        } else {
          console.warn('No snapshot data in response:', res.data);
          setPreviewSnapshot([]);
          setIsPreview(false);
        }
      } catch (err) {
        console.error('Error loading version preview:', err);
        showSnackbar('Failed to load version preview', 'error');
        setPreviewSnapshot([]);
        setIsPreview(false);
      }
    }
  };

  // Khi muốn quay lại dữ liệu thật
  const handleBackToLive = () => {
    setSelectedVersion(null);
    setIsPreview(false);
    setPreviewSnapshot([]);
    fetchCampaignData(); // Load lại dữ liệu thật
  };

  // Helper function to compare cell values and determine if there's a diff
  const getCellDiff = (currentRow, versionRow, fieldName) => {
    if (!isPreview || !versionRow) {
      return { hasChange: false };
    }

    const currentValue = currentRow[fieldName];
    const versionValue = versionRow[fieldName];

    // Handle null/undefined values
    const normalizedCurrent = currentValue === null || currentValue === undefined ? '' : String(currentValue);
    const normalizedVersion = versionValue === null || versionValue === undefined ? '' : String(versionValue);

    // For numeric fields, compare as numbers to avoid false positives like "0" vs "0.00"
    const numericFields = [
      'given', 'planning_gross_cost', 'actual_gross_cost', 'net_net_cost', 
      'tracking_fees', 'whtax', 'ooscope_fees', 'saving', 'coupon',
      'ntw', 'north', 'central_mid', 'the_rest_of_central', 'ghcm', 'mekong'
    ];

    if (numericFields.includes(fieldName)) {
      const currentNum = parseFloat(currentValue) || 0;
      const versionNum = parseFloat(versionValue) || 0;
      
      // Consider values the same if difference is less than 0.01 (1 cent)
      const difference = Math.abs(currentNum - versionNum);
      if (difference < 0.01) {
        return { hasChange: false };
      }
      
      // Only show diff if there's a meaningful difference
      if (difference >= 0.01) {
        return {
          hasChange: true,
          oldValue: versionValue,
          newValue: currentValue,
          field: fieldName
        };
      }
    } else {
      // For non-numeric fields, do string comparison but trim whitespace
      const trimmedCurrent = normalizedCurrent.trim();
      const trimmedVersion = normalizedVersion.trim();
      
      if (trimmedCurrent !== trimmedVersion) {
        return {
          hasChange: true,
          oldValue: versionValue,
          newValue: currentValue,
          field: fieldName
        };
      }
    }

    return { hasChange: false };
  };

  // Find corresponding row in version snapshot by ID or other unique identifier
  const findVersionRow = (currentRow) => {
    if (!isPreview || !previewSnapshot) {
      return null;
    }

    // Ensure previewSnapshot is an array and has content
    if (!Array.isArray(previewSnapshot) || previewSnapshot.length === 0) {
      return null;
    }

    // Try to find by ID first
    let versionRow = previewSnapshot.find(row => row.id === currentRow.id);
    
    // If not found by ID, try to find by month and other identifying fields
    if (!versionRow) {
      versionRow = previewSnapshot.find(row => 
        row.month === currentRow.month &&
        row.media_type === currentRow.media_type &&
        row.digital_vendor === currentRow.digital_vendor &&
        row.region_focus === currentRow.region_focus
      );
    }

    return versionRow;
  };

  // Get diff styling for table cells
  const getDiffCellStyle = (diff) => {
    if (!diff.hasChange) {
      return {};
    }

    return {
      backgroundColor: '#ffebee', // Light red background
      borderLeft: '3px solid #f44336', // Red left border indicator
      position: 'relative'
    };
  };

  // Format diff tooltip content
  const formatDiffTooltip = (diff) => {
    if (!diff.hasChange) {
      return '';
    }

    // Check if this is a numeric field for better formatting
    const numericFields = [
      'given', 'planning_gross_cost', 'actual_gross_cost', 'net_net_cost', 
      'tracking_fees', 'whtax', 'ooscope_fees', 'saving', 'coupon',
      'ntw', 'north', 'central_mid', 'the_rest_of_central', 'ghcm', 'mekong'
    ];

    let oldValueFormatted, newValueFormatted;

    if (numericFields.includes(diff.field)) {
      // Format currency values
      oldValueFormatted = diff.oldValue === null || diff.oldValue === undefined ? 'Empty' : formatCurrency(diff.oldValue);
      newValueFormatted = diff.newValue === null || diff.newValue === undefined ? 'Empty' : formatCurrency(diff.newValue);
    } else {
      // Format other values
      oldValueFormatted = diff.oldValue === null || diff.oldValue === undefined ? 'Empty' : String(diff.oldValue);
      newValueFormatted = diff.newValue === null || diff.newValue === undefined ? 'Empty' : String(diff.newValue);
    }

    return `Changed from "${oldValueFormatted}" to "${newValueFormatted}"`;
  };

  // Enhanced TableCell component with diff highlighting
  const DiffTableCell = ({ children, row, fieldName, align = 'left', sx = {}, ...props }) => {
    const versionRow = findVersionRow(row);
    const diff = getCellDiff(row, versionRow, fieldName);
    const diffStyle = getDiffCellStyle(diff);
    const tooltipText = formatDiffTooltip(diff);

    const cellContent = (
      <TableCell 
        align={align} 
        sx={{ 
          ...sx, 
          ...diffStyle,
          ...(diff.hasChange && {
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 2,
              right: 2,
              width: 8,
              height: 8,
              backgroundColor: '#f44336',
              borderRadius: '50%',
              zIndex: 1
            }
          })
        }} 
        {...props}
      >
        {children}
      </TableCell>
    );

    if (diff.hasChange && tooltipText) {
      return (
        <Tooltip title={tooltipText} arrow placement="top">
          {cellContent}
        </Tooltip>
      );
    }

    return cellContent;
  };

  // Toggle edit mode with proper state management
  const toggleEditMode = () => {
    if (!editState.isEditing) {
      // Entering edit mode - save current data as original
      setEditState(prev => ({
        ...prev,
        isEditing: true,
        originalData: accrualData.map(row => ({ ...row })),
        editedData: accrualData.map(row => ({ ...row })),
        pendingChanges: new Set(),
        deletedIds: [],
        hasUnsavedChanges: false
      }));
    } else {
      // Exiting edit mode - confirm if there are unsaved changes
      if (editState.hasUnsavedChanges) {
        if (window.confirm('You have unsaved changes. Are you sure you want to exit edit mode?')) {
          resetEditState();
        }
      } else {
        resetEditState();
      }
    }
  };

  const resetEditState = () => {
    setEditState({
      isEditing: false,
      originalData: [],
      editedData: [],
      pendingChanges: new Set(),
      deletedIds: [],
      hasUnsavedChanges: false
    });
  };

  // Handle field change with improved tracking
  const handleFieldChange = async (rowIndex, field, value) => {
    setEditState(prev => {
      const newEditedData = [...prev.editedData];
      const updatedRow = { ...newEditedData[rowIndex] };
      
      // 🔧 FIX: Update field value
      updatedRow[field] = value;
      
      // Recalculate dependent fields based on which field changed
      if (field === 'sub_media_type') {
        // Let backend handle uniqueness, just update locally for now
        updatedRow[field] = value;
      } else if (['net_net_cost', 'whtax', 'ooscope_fees', 'tracking_fees', 'cost_on_coupon_voucher_etc'].includes(field)) {
        // Recalculate actual_gross_cost when component fields change
        updatedRow.actual_gross_cost = calculateActualGrossCost(updatedRow);
        
        // Also recalculate saving and unused when actual_gross_cost changes
        updatedRow.unused = calculateUnused(updatedRow);
        
        // 🔧 NEW: Auto-update monthly_status to "actual" when actual data exists
        // Logic: If any component costs > 0, automatically change status from "planning" to "actual"
        // This ensures monthly_status reflects the actual data state
        const hasActualData = (
          (typeof updatedRow.net_net_cost === 'number' && updatedRow.net_net_cost > 0) ||
          (typeof updatedRow.whtax === 'number' && updatedRow.whtax > 0) ||
          (typeof updatedRow.ooscope_fees === 'number' && updatedRow.ooscope_fees > 0) ||
          (typeof updatedRow.tracking_fees === 'number' && updatedRow.tracking_fees > 0) ||
          (typeof updatedRow.cost_on_coupon_voucher_etc === 'number' && updatedRow.cost_on_coupon_voucher_etc > 0) ||
          (typeof updatedRow.actual_gross_cost === 'number' && updatedRow.actual_gross_cost > 0)
        );
        
        if (hasActualData && updatedRow.monthly_status === 'planning') {
          updatedRow.monthly_status = 'actual';
          console.log(`🔄 Auto-updated monthly_status to "actual" for row ${rowIndex} - actual data detected`);
        }
        
        // Only compute saving when status is actual; otherwise show NA
        if (updatedRow.monthly_status === 'actual') {
          if (updatedRow.provisional_saving !== undefined && updatedRow.provisional_saving !== null) {
            updatedRow.saving = parseFloat(updatedRow.provisional_saving) || 0;
            updatedRow.savingDisplay = undefined;
          } else {
            const planningNum = Number(updatedRow.planning_gross_cost) || 0;
            const actualNum = Number(updatedRow.actual_gross_cost) || 0;
            updatedRow.saving = planningNum - actualNum;
            updatedRow.savingDisplay = undefined;
          }
          updatedRow.savingPercentage = calculateSavingPercentage(updatedRow, campaign, updatedRow.actual_gross_cost);
        } else {
          updatedRow.saving = 0;
          updatedRow.savingDisplay = 'NA';
          updatedRow.savingPercentage = { percentage: 0, display: 'NA', style: {} };
        }
      } else if (field === 'monthly_status') {
        // 🔧 NEW: Handle monthly_status change manually
        // When user manually changes monthly_status to "actual", recalculate saving immediately
        if (value === 'actual') {
          console.log(`🔄 Manual monthly_status change to "actual" for row ${rowIndex}`);
          
          // Recalculate saving based on new status
          const hasActualInputs = (
            (updatedRow.has_actual_inputs === true) ||
            (typeof updatedRow.net_net_cost === 'number' && updatedRow.net_net_cost > 0) ||
            (typeof updatedRow.whtax === 'number' && updatedRow.whtax > 0) ||
            (typeof updatedRow.ooscope_fees === 'number' && updatedRow.ooscope_fees > 0) ||
            (typeof updatedRow.tracking_fees === 'number' && updatedRow.tracking_fees > 0) ||
            (typeof updatedRow.cost_on_coupon_voucher_etc === 'number' && updatedRow.cost_on_coupon_voucher_etc > 0) ||
            (typeof updatedRow.actual_gross_cost === 'number' && updatedRow.actual_gross_cost > 0)
          );
          
          // Always compute saving when status is actual, even if actual is 0
          const planningNum = Number(updatedRow.planning_gross_cost) || 0;
          const actualNum = Number(updatedRow.actual_gross_cost) || 0; // allow 0
          updatedRow.saving = planningNum - actualNum;
          updatedRow.savingDisplay = undefined;
          console.log(`💰 Recalculated saving: ${updatedRow.saving} (planning: ${planningNum}, actual: ${actualNum})`);
          
          // Recalculate saving percentage
          updatedRow.savingPercentage = hasActualInputs
            ? calculateSavingPercentage(updatedRow, campaign, updatedRow.actual_gross_cost)
            : { percentage: 0, display: 'NA', style: {} };
        } else if (value === 'planning') {
          // When status changes back to "planning", reset saving
          updatedRow.saving = 0;
          updatedRow.savingDisplay = 'NA';
          updatedRow.savingPercentage = { percentage: 0, display: 'NA', style: {} };
          console.log(`🔄 Reset saving for row ${rowIndex} - status changed to "planning"`);
        } else if (value === 'actual') {
          // Already handled above; keep branch for safety but ensure same behavior
          updatedRow.actual_gross_cost = calculateActualGrossCost(updatedRow);
          const planningNum = Number(updatedRow.planning_gross_cost) || 0;
          const actualNum = Number(updatedRow.actual_gross_cost) || 0; // allow 0
          updatedRow.saving = planningNum - actualNum;
          updatedRow.savingDisplay = undefined;
          updatedRow.savingPercentage = calculateSavingPercentage(updatedRow, campaign, updatedRow.actual_gross_cost);
        }
      } else if (['ntw', 'north', 'central_mid', 'the_rest_of_central', 'ghcm', 'mekong'].includes(field)) {
        // 🔧 FIX: Recalculate check value when regional fields change
        updatedRow.check = calculateCheck(updatedRow);
      } else if (['given', 'planning_gross_cost'].includes(field)) {
        // Recalculate unused when budget-related fields change
        updatedRow.unused = calculateUnused(updatedRow);
        
        // 🔧 NEW: Auto-update monthly_status to "actual" when actual data exists
        // Logic: If any component costs > 0, automatically change status from "planning" to "actual"
        // This ensures monthly_status reflects the actual data state
        const hasActualData = (
          (typeof updatedRow.net_net_cost === 'number' && updatedRow.net_net_cost > 0) ||
          (typeof updatedRow.whtax === 'number' && updatedRow.whtax > 0) ||
          (typeof updatedRow.ooscope_fees === 'number' && updatedRow.ooscope_fees > 0) ||
          (typeof updatedRow.tracking_fees === 'number' && updatedRow.tracking_fees > 0) ||
          (typeof updatedRow.cost_on_coupon_voucher_etc === 'number' && updatedRow.cost_on_coupon_voucher_etc > 0) ||
          (typeof updatedRow.actual_gross_cost === 'number' && updatedRow.actual_gross_cost > 0)
        );
        
        if (hasActualData && updatedRow.monthly_status === 'planning') {
          updatedRow.monthly_status = 'actual';
          console.log(`🔄 Auto-updated monthly_status to "actual" for row ${rowIndex} - actual data detected`);
        }
        
        // Only compute saving when status is actual; otherwise show NA
        if (updatedRow.monthly_status === 'actual') {
          if (updatedRow.provisional_saving !== undefined && updatedRow.provisional_saving !== null) {
            updatedRow.saving = parseFloat(updatedRow.provisional_saving) || 0;
            updatedRow.savingDisplay = undefined;
          } else {
            const planningNum = Number(updatedRow.planning_gross_cost) || 0;
            const actualNum = Number(updatedRow.actual_gross_cost) || 0;
            updatedRow.saving = planningNum - actualNum;
            updatedRow.savingDisplay = undefined;
          }
          updatedRow.savingPercentage = calculateSavingPercentage(updatedRow, campaign, updatedRow.actual_gross_cost);
        } else {
          updatedRow.saving = 0;
          updatedRow.savingDisplay = 'NA';
          updatedRow.savingPercentage = { percentage: 0, display: 'NA', style: {} };
        }
      }
      
      newEditedData[rowIndex] = updatedRow;
      
      // Track changes
      const newPendingChanges = new Set(prev.pendingChanges);
      newPendingChanges.add(rowIndex);
      
      return {
        ...prev,
        editedData: newEditedData,
        pendingChanges: newPendingChanges,
        hasUnsavedChanges: true
      };
    });
  };

  // Handle delete row with explicit tracking
  const handleDeleteRow = (rowIndex) => {
    setEditState(prev => {
      const rowToDelete = prev.editedData[rowIndex];
      const newEditedData = [...prev.editedData];
      
      // Remove from edited data
      newEditedData.splice(rowIndex, 1);
      
      // Add to deleted IDs if it's an existing row
      const newDeletedIds = [...prev.deletedIds];
      if (rowToDelete.id && typeof rowToDelete.id !== 'string') {
        newDeletedIds.push(rowToDelete.id);
      }
      
      return {
        ...prev,
        editedData: newEditedData,
        deletedIds: newDeletedIds,
        hasUnsavedChanges: true
      };
    });
    
    showSnackbar('Row marked for deletion', 'success');
  };

  const handleMouseMove = (e) => {
    if (isResizing && resizingColumn) {
      const newWidth = Math.max(50, startWidth + (e.clientX - startX)); // Min width 50px
      setColumnWidths(prev => ({
        ...prev,
        [resizingColumn]: newWidth
      }));
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
    setResizingColumn(null);
    setStartX(0);
    setStartWidth(0);
  };

  // Handle mouse down for column resizing
  const handleMouseDown = (e, columnKey) => {
    e.preventDefault();
    setIsResizing(true);
    setResizingColumn(columnKey);
    setStartX(e.clientX);
    setStartWidth(columnWidths[columnKey]);
  };

  // Check campaign availability function
  const checkCampaignAvailability = async () => {
    try {
      console.log('Checking campaign availability...');
      // This function would typically redirect to campaign list or show available campaigns
      showSnackbar('Campaign not found in this context. Redirecting to campaign list...', 'warning');
      setTimeout(() => {
        navigate('/campaigns');
      }, 2000);
    } catch (error) {
      console.error('Error checking campaign availability:', error);
      showSnackbar('Error checking campaign availability', 'error');
    }
  };

  // Lock toggle handler
  const handleLockToggle = async (rowId, isLocked) => {
    try {
      if (isLocked) {
        // Row is locked, user wants to unlock - create unlock request
        await handleUnlockRequest(rowId);
      } else {
        // Row is unlocked, user wants to lock - directly lock it
        await handleLockRow(rowId);
      }
    } catch (error) {
      console.error('Error toggling lock:', error);
      showSnackbar('Error toggling row lock status', 'error');
    }
  };

  // Handle locking a row (direct lock)
  const handleLockRow = async (rowId) => {
    try {
      console.log(`Locking row ${rowId}`);
      
      // Call API to lock the row
      await lockUnlockApi.lockRow(rowId);
      
      // Update local state
      const updatedData = accrualData.map(row => {
        if (row.id === rowId) {
          return { ...row, is_locked: true };
        }
        return row;
      });
      
      setAccrualData(updatedData);
      
      // Also update editedData if in edit mode
      if (editState.isEditing) {
        setEditState(prev => ({
          ...prev,
          editedData: prev.editedData.map(row => 
            row.id === rowId ? { ...row, is_locked: true } : row
          )
        }));
      }
      
      showSnackbar('Row locked successfully', 'success');
      
    } catch (error) {
      console.error('Error locking row:', error);
      showSnackbar('Error locking row: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  // Handle unlock request workflow
  const handleUnlockRequest = async (rowId) => {
    try {
      // Check if there's already a pending request for this row
      const pendingRequests = await lockUnlockApi.getUnlockRequestsByRow(rowId, 'pending');
      
      if (pendingRequests.data && pendingRequests.data.length > 0) {
        showSnackbar('There is already a pending unlock request for this row', 'warning');
        return;
      }
      
      // Show dialog to get reason and attachment for unlock request
      const requestData = await showUnlockRequestDialog(rowId);
      
      if (requestData) {
        // Create unlock request with attachment
        await lockUnlockApi.createUnlockRequestWithAttachment(rowId, requestData.reason, requestData.attachment);
        
        // Update unlock status state
        setRowUnlockStatus(prev => ({
          ...prev,
          [rowId]: 'pending'
        }));
        
        const attachmentText = requestData.attachment ? ' with attachment' : '';
        showSnackbar(`Unlock request${attachmentText} submitted successfully. Admin will review your request.`, 'success');
        
        // Refresh unlock status for all rows
        await loadUnlockStatuses();
      }
      
    } catch (error) {
      console.error('Error creating unlock request:', error);
      showSnackbar('Error creating unlock request: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  // Show unlock request dialog
  const showUnlockRequestDialog = (rowId) => {
    return new Promise((resolve) => {
      setUnlockRowId(rowId);
      setUnlockReason('');
      setUnlockAttachment(null);
      setAttachmentError('');
      setUploadingFile(false);
      setShowUnlockDialog(true);
      
      // Store resolve function for dialog completion
      window.unlockDialogResolve = resolve;
    });
  };

  // Handle unlock dialog submit
  const handleUnlockDialogSubmit = () => {
    if (unlockReason.trim()) {
      window.unlockDialogResolve({ 
        reason: unlockReason.trim(), 
        attachment: unlockAttachment 
      });
      setShowUnlockDialog(false);
      setUnlockRowId(null);
      setUnlockReason('');
      setUnlockAttachment(null);
      setAttachmentError('');
      setUploadingFile(false);
    }
  };

  // Handle unlock dialog cancel
  const handleUnlockDialogCancel = () => {
    window.unlockDialogResolve(null);
    setShowUnlockDialog(false);
    setUnlockRowId(null);
    setUnlockReason('');
    setUnlockAttachment(null);
    setAttachmentError('');
    setUploadingFile(false);
  };

  // Handle file upload for unlock request
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setAttachmentError('');
    setUploadingFile(true);
    
    // Simulate loading delay for better UX
    setTimeout(() => {
      if (!file) {
        setUnlockAttachment(null);
        setUploadingFile(false);
        return;
      }
      
      // Validate file type (only PDF)
      if (file.type !== 'application/pdf') {
        setAttachmentError('Only PDF files are allowed. Please select a PDF file.');
        setUnlockAttachment(null);
        event.target.value = ''; // Clear file input
        setUploadingFile(false);
        return;
      }
      
      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        setAttachmentError('File size must be less than 10MB. Please select a smaller file.');
        setUnlockAttachment(null);
        event.target.value = ''; // Clear file input
        setUploadingFile(false);
        return;
      }
      
      // File is valid
      setUnlockAttachment(file);
      setAttachmentError('');
      setUploadingFile(false);
    }, 500);
  };

  // Remove uploaded file
  const handleRemoveFile = () => {
    setUnlockAttachment(null);
    setAttachmentError('');
    setUploadingFile(false);
    // Clear file input
    const fileInput = document.getElementById('unlock-attachment-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Handle drag and drop for file upload
  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    
    if (files.length > 0) {
      const file = files[0];
      setUploadingFile(true);
      
      // Simulate loading delay for better UX
      setTimeout(() => {
        // Validate file type (only PDF)
        if (file.type !== 'application/pdf') {
          setAttachmentError('Only PDF files are allowed. Please select a PDF file.');
          setUnlockAttachment(null);
          setUploadingFile(false);
          return;
        }
        
        // Validate file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (file.size > maxSize) {
          setAttachmentError('File size must be less than 10MB. Please select a smaller file.');
          setUnlockAttachment(null);
          setUploadingFile(false);
          return;
        }
        
        // File is valid
        setUnlockAttachment(file);
        setAttachmentError('');
        setUploadingFile(false);
      }, 500);
    }
  };

  // Load unlock statuses for all rows
  const loadUnlockStatuses = async () => {
    try {
      if (!accrualData || accrualData.length === 0) return;
      
      const rowIds = accrualData.map(row => row.id);
      const unlockStatus = await lockUnlockApi.getRowsUnlockStatus(rowIds);
      
      // Convert to simple status format
      const statusMap = {};
      Object.entries(unlockStatus).forEach(([rowId, requests]) => {
        if (requests && requests.length > 0) {
          // Check if there are pending requests
          const hasPending = requests.some(req => req.status === 'pending');
          statusMap[rowId] = hasPending ? 'pending' : 'processed';
        }
      });
      
      setRowUnlockStatus(statusMap);
      
    } catch (error) {
      console.error('Error loading unlock statuses:', error);
    }
  };

  // Load unlock statuses when data changes
  useEffect(() => {
    if (accrualData.length > 0 && !isSnapshotView) {
      loadUnlockStatuses();
    }
  }, [accrualData, isSnapshotView]);

  // Refresh unlock statuses periodically and check for updates
  useEffect(() => {
    if (!isSnapshotView) {
      const interval = setInterval(async () => {
        const previousStatus = { ...rowUnlockStatus };
        await loadUnlockStatuses();
        
        // Check if any status changed from pending to something else
        Object.entries(previousStatus).forEach(([rowId, prevStatus]) => {
          const currentStatus = rowUnlockStatus[rowId];
          if (prevStatus === 'pending' && currentStatus !== 'pending') {
            // Status changed, refresh the row data to get updated lock status
            fetchCampaignData();
            
            // Show notification based on new status
            if (currentStatus === 'processed') {
              showSnackbar('Your unlock request has been processed. Check if the row is now unlocked.', 'info');
            }
          }
        });
      }, 30000); // Every 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [isSnapshotView, rowUnlockStatus]);

  // Data menu handlers
  const handleDataMenuOpen = (event, row) => {
    event.stopPropagation();
    setDataMenuAnchor(event.currentTarget);
    setSelectedRowForMenu(row);
  };

  const handleDataMenuClose = () => {
    setDataMenuAnchor(null);
    setSelectedRowForMenu(null);
  };

  // Add row handler
  const handleAddRow = async () => {
    try {
      if (!campaign) {
        showSnackbar('Campaign data not loaded', 'error');
        return;
      }

      // 🔧 FIX: Check if campaign has media type setup first
      if (!accrualData || accrualData.length === 0) {
        console.log('🔧 No existing accrual data - checking/creating campaign media type...');
        
        try {
          // Check if campaign media type exists for this media type
          const mediaTypeResponse = await campaignApi.getCampaignMediaTypes(campaign.id);
          const existingMediaTypes = mediaTypeResponse?.data || [];
          
          console.log('📋 Existing media types for campaign:', existingMediaTypes);
          
          // Check if current mediaType already exists
          const hasCurrentMediaType = existingMediaTypes.some(mt => 
            mt.media_type && mt.media_type.toLowerCase() === mediaType.toLowerCase()
          );
          
          if (!hasCurrentMediaType) {
            console.log(`🔧 Creating campaign media type for: ${mediaType}`);
            
            // Create campaign media type first
            const createMediaTypeResponse = await api.post(`/campaigns-data/${campaign.id}/media-types/`, {
              media_type: mediaType
            });
            
            console.log('✅ Campaign media type created:', createMediaTypeResponse.data);
            
            // Refresh campaign data to get the new media type
            await fetchCampaignData();
            
            showSnackbar(`Media type "${mediaType}" added to campaign. You can now add rows.`, 'success');
            return; // Exit here - user needs to click Add Row again after media type is created
          }
        } catch (error) {
          console.error('❌ Error checking/creating campaign media type:', error);
          showSnackbar('Error setting up campaign media type. Please try again.', 'error');
          return;
        }
      }

      // 🔧 FIX: Generate unique ID with random component to prevent duplicates
      const uniqueId = `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      console.log('🆕 Creating new row with ID:', uniqueId);

      // 🔧 FIX: Get campaign_media_type ID for this media type
      let campaignMediaTypeId = null;
      
      if (accrualData && accrualData.length > 0) {
        // Use campaign_media_type from existing data
        campaignMediaTypeId = accrualData[0].campaign_media_type;
      } else {
        // Get campaign media type ID from campaign media types
        try {
          const mediaTypeResponse = await campaignApi.getCampaignMediaTypes(campaign.id);
          const existingMediaTypes = mediaTypeResponse?.data || [];
          const currentMediaTypeRecord = existingMediaTypes.find(mt => 
            mt.media_type && mt.media_type.toLowerCase() === mediaType.toLowerCase()
          );
          campaignMediaTypeId = currentMediaTypeRecord?.id;
        } catch (error) {
          console.error('❌ Error getting campaign media type ID:', error);
        }
      }
      
      if (!campaignMediaTypeId) {
        showSnackbar('Cannot determine campaign media type. Please refresh the page.', 'error');
        return;
      }

      // 🔧 FIX: Create default row without relying on monthRange
      const newRow = {
        id: uniqueId, // Temporary ID for frontend tracking (will be removed before saving)
        campaign_media_type: campaignMediaTypeId, // Required field from backend
        media_type: mediaType || 'Digital', // Default media type
        sub_media_type: '', // Required field, will be set by user
        digital_vendor: '',
        digital_ad_format: '',
        ooh_format: '',
        region_focus: '',
        asset_video_length_image_type: '',
        month: '', // Will be set via dropdown - required field
        monthly_status: 'planning',
        placement: '',
        start_date: campaign.start_date_actual || campaign.start_date,
        end_date: campaign.end_date_actual || campaign.end_date,
        status: 'Active',
        is_locked: false,
        // 🔧 FIX: Initialize all currency fields to 0 (as numbers, not strings)
        given: 0,
        planning_gross_cost: 0,
        net_net_cost: 0,
        tracking_fees: 0,
        whtax: 0,
        ooscope_fees: 0,
        cost_on_coupon_voucher_etc: 0,
        coupon: 0,
        po_number: '',
        po_cost: 0,
        cost_on_ecpms: 0,
        ntw: 0,
        north: 0,
        central_mid: 0,
        the_rest_of_central: 0,
        ghcm: 0,
        mekong: 0,
        // 🔧 NOTE: Calculated fields (actual_gross_cost, unused, saving, savingPercentage, check) 
        // will be added by processAccrualData after row is created
      };

      console.log('🔄 Current editState before adding:', {
        isEditing: editState.isEditing,
        editedDataLength: editState.editedData.length,
        accrualDataLength: accrualData.length
      });

      // Process the new row to add calculated fields
      const processedNewRow = processAccrualDataWithCampaign([newRow], campaign)[0];

      // If not in edit mode, enter edit mode first
      if (!editState.isEditing) {
        console.log('🔓 Entering edit mode and adding first row');
        setEditState(prev => ({
          ...prev,
          isEditing: true,
          originalData: accrualData.map(row => ({ ...row })),
          editedData: [...accrualData.map(row => ({ ...row })), processedNewRow],
          pendingChanges: new Set([accrualData.length]), // Mark the new row as changed
          deletedIds: [],
          hasUnsavedChanges: true
        }));
      } else {
        // Already in edit mode, just add to editedData
        console.log('✏️ Already in edit mode, adding additional row');
        setEditState(prev => {
          const newEditedData = [...prev.editedData, processedNewRow];
          const newPendingChanges = new Set(prev.pendingChanges);
          newPendingChanges.add(newEditedData.length - 1); // Mark the new row as changed
          
          console.log('📝 Updated editedData length:', newEditedData.length);
          console.log('🔄 New row will be at index:', newEditedData.length - 1);
          
          return {
            ...prev,
            editedData: newEditedData,
            pendingChanges: newPendingChanges,
            hasUnsavedChanges: true
          };
        });
      }

      showSnackbar(`New row added (ID: ${uniqueId})`, 'success');
      console.log('✅ New row added successfully');
    } catch (error) {
      console.error('❌ Error adding row:', error);
      showSnackbar('Error adding new row: ' + error.message, 'error');
    }
  };


  // No longer handling sequence numbers for sub_media_type - backend handles uniqueness with row_identifier

  // Save changes handler
  const handleSaveChanges = async () => {
    try {
      if (!editState.hasUnsavedChanges) {
        showSnackbar('No changes to save', 'info');
        return;
      }

      setLoading(true);
      console.log('Saving changes...', editState);

      // 🔧 FIX: Separate new rows and existing rows for proper backend handling
      console.log('Preparing data for save - separating new and existing rows...');
      
      // Get all current data (existing rows)
       const allCurrentData = accrualData || [];
      
      // Separate edited data into new rows and existing rows
      const newRows = [];
      const existingRows = [];
      
      editState.editedData.forEach((row, index) => {
        if (row.id && row.id.toString().startsWith('new_')) {
          // This is a new row - remove the temporary ID and let backend generate real ID
          console.log('🆕 Processing new row:', row.id);
          const newRow = { ...row };
          delete newRow.id; // Remove temporary ID so backend can generate real one
          newRows.push(newRow);
        } else if (index < allCurrentData.length) {
          // This is an existing row that was edited
          console.log('✏️ Processing existing row:', row.id);
          existingRows.push(row);
        }
      });

      // Also include unchanged existing rows
      allCurrentData.forEach((row, index) => {
        if (index >= editState.editedData.length || !editState.editedData[index]) {
          // This row wasn't in editedData, so it's unchanged
          existingRows.push(row);
        }
      });

      console.log('📊 Data breakdown:', {
        newRowsCount: newRows.length,
        existingRowsCount: existingRows.length,
        totalOriginalRows: allCurrentData.length,
        totalEditedRows: editState.editedData.length
      });

      // No longer applying sequence numbers - backend handles uniqueness with row_identifier
      const newRowsWithSequences = newRows;
      const existingRowsWithSequences = existingRows;
      
      // 🔧 FIX: Filter out frontend-only calculated fields before sending to backend
      const cleanDataForBackend = (data) => {
        return data.map(row => {
          const cleanRow = { ...row };
          // Keep monthly_status as-is (planning/actual). Backend should accept 'actual'.
          
          // Remove frontend-only calculated fields that backend doesn't expect
          delete cleanRow.check;
          delete cleanRow.savingPercentage;
          delete cleanRow.actual_gross_cost; // This should be calculated by backend
          delete cleanRow.unused; // This should be calculated by backend  
          delete cleanRow.saving; // This should be calculated by backend
          
          // Ensure proper data types for key fields
          if (cleanRow.campaign_media_type) {
            cleanRow.campaign_media_type = parseInt(cleanRow.campaign_media_type);
          }
          
          // Ensure numeric fields are numbers, not strings
          const numericFields = [
            'given', 'planning_gross_cost', 'net_net_cost', 'tracking_fees', 
            'whtax', 'ooscope_fees', 'cost_on_coupon_voucher_etc', 'coupon',
            'po_cost', 'cost_on_ecpms', 'ntw', 'north', 'central_mid', 
            'the_rest_of_central', 'ghcm', 'mekong'
          ];
          
          numericFields.forEach(field => {
            if (cleanRow[field] !== undefined && cleanRow[field] !== null) {
              cleanRow[field] = parseFloat(cleanRow[field]) || 0;
            }
          });
          
          // Ensure boolean fields are proper booleans
          if (cleanRow.is_locked !== undefined) {
            cleanRow.is_locked = Boolean(cleanRow.is_locked);
          }
          
          return cleanRow;
        });
      };
      
      // Clean the data before saving
      const cleanNewRows = cleanDataForBackend(newRowsWithSequences);
      const cleanExistingRows = cleanDataForBackend(existingRowsWithSequences);
      
      // Combine all data for saving
      const finalDataToSave = [...cleanExistingRows, ...cleanNewRows];
      
             console.log('📤 Final data to save:', {
         totalRows: finalDataToSave.length,
         newRows: cleanNewRows.length,
         existingRows: cleanExistingRows.length,
         sampleNewRow: cleanNewRows[0] ? { 
           id: cleanNewRows[0].id, 
           campaign_media_type: cleanNewRows[0].campaign_media_type,
           media_type: cleanNewRows[0].media_type,
           sub_media_type: cleanNewRows[0].sub_media_type,
           month: cleanNewRows[0].month,
           hasCheck: 'check' in cleanNewRows[0],
           hasSavingPercentage: 'savingPercentage' in cleanNewRows[0]
         } : 'none',
         sampleExistingRow: cleanExistingRows[0] ? {
           id: cleanExistingRows[0].id,
           campaign_media_type: cleanExistingRows[0].campaign_media_type,
           media_type: cleanExistingRows[0].media_type,
           hasCheck: 'check' in cleanExistingRows[0],
           hasSavingPercentage: 'savingPercentage' in cleanExistingRows[0]
         } : 'none'
       });
       
       // 🔧 DEBUG: Log the exact payload being sent to backend
       console.log('🔍 Raw payload preview (first 2 rows):', 
         finalDataToSave.slice(0, 2).map(row => ({
           id: row.id,
           hasId: row.hasOwnProperty('id'),
           idType: typeof row.id,
           campaign_media_type: row.campaign_media_type,
           campaign_media_type_type: typeof row.campaign_media_type,
           media_type: row.media_type,
           month: row.month,
           // Verify problematic fields are removed
           hasCheck: 'check' in row,
           hasSavingPercentage: 'savingPercentage' in row,
           hasActualGrossCost: 'actual_gross_cost' in row,
           allKeys: Object.keys(row).length
         }))
       );
       
       // 🔧 ENHANCED DEBUG: Log first complete row for detailed analysis
       if (finalDataToSave.length > 0) {
         console.log('🔍 Complete first row payload:', finalDataToSave[0]);
         console.log('🔍 All fields in first row:', Object.keys(finalDataToSave[0]));
         console.log('🔍 Required fields check:', {
           hasCampaignMediaType: 'campaign_media_type' in finalDataToSave[0],
           hasMediaType: 'media_type' in finalDataToSave[0],
           hasMonth: 'month' in finalDataToSave[0],
           campaignMediaTypeValue: finalDataToSave[0].campaign_media_type,
           mediaTypeValue: finalDataToSave[0].media_type,
           monthValue: finalDataToSave[0].month
         });
         
                   // 🔧 VALIDATION: Check for common issues
          const validationIssues = [];
          finalDataToSave.forEach((row, index) => {
            if (!row.campaign_media_type) validationIssues.push(`Row ${index}: Missing campaign_media_type`);
            if (!row.media_type) validationIssues.push(`Row ${index}: Missing media_type`);
            if (!row.month) validationIssues.push(`Row ${index}: Missing month`);
            if (typeof row.campaign_media_type !== 'number') validationIssues.push(`Row ${index}: campaign_media_type is not number (${typeof row.campaign_media_type})`);
            if (row.hasOwnProperty('check')) validationIssues.push(`Row ${index}: Still has 'check' field`);
            if (row.hasOwnProperty('savingPercentage')) validationIssues.push(`Row ${index}: Still has 'savingPercentage' field`);
          });
         
         if (validationIssues.length > 0) {
           console.error('❌ VALIDATION ISSUES FOUND:', validationIssues);
         } else {
           console.log('✅ All validation checks passed');
         }
       }

      // Handle saves and deletes separately for UUID endpoint
      const savePromises = [];
      
      // Save/update data if there's any
      if (finalDataToSave.length > 0) {
        savePromises.push(accrualApi.saveAccrualData(campaignId, finalDataToSave));
      }

      // Handle deleted rows separately
      if (editState.deletedIds && editState.deletedIds.length > 0) {
        console.log('🗑️ Deleting rows separately:', editState.deletedIds);
        const deletePromises = editState.deletedIds.map(id => 
          accrualApi.deleteAccrualData(campaignId, id)
        );
        savePromises.push(...deletePromises);
      }

      // Execute all save and delete operations
      const results = await Promise.all(savePromises);
      console.log('💾 Save results:', results);
      
      showSnackbar('Changes saved successfully', 'success');
      
      // Reset edit state
      resetEditState();
      setIsEditMode(false);
      
      // Refresh data
      await fetchCampaignData();
      
    } catch (error) {
      console.error('❌ Error saving changes:', error);
      
      // 🔧 ENHANCED DEBUG: Log complete error details  
      console.error('📋 Complete Error Object:', {
        message: error.message,
        name: error.name,
        code: error.code,
        hasResponse: !!error.response,
        responseStatus: error.response?.status,
        responseData: error.response?.data,
        responseHeaders: error.response?.headers,
        config: error.config
      });
      
      // 🔧 FIX: Better error handling for backend validation errors
      let errorMessage = 'Error saving changes';
      
      if (error.response) {
        console.error('📋 Response Error Details:', error.response.data);
        
        if (error.response.data?.errors && Array.isArray(error.response.data.errors)) {
          // Handle multiple validation errors
          const errorDetails = error.response.data.errors.map(err => 
            `Row ${err.row}: ${err.error}`
          ).join('\n');
          errorMessage = `Validation errors:\n${errorDetails}`;
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data) {
          errorMessage = `Server error: ${JSON.stringify(error.response.data)}`;
        } else {
          errorMessage = `Server error (${error.response.status}): ${error.response.statusText}`;
        }
      } else if (error.request) {
        console.error('📋 Network Error - No Response:', error.request);
        errorMessage = 'Network error - unable to reach server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // View history handler
  const handleViewHistory = (rowId) => {
    try {
      console.log(`Viewing history for row ${rowId}`);
      showSnackbar('History view functionality coming soon', 'info');
      handleDataMenuClose();
    } catch (error) {
      console.error('Error viewing history:', error);
      showSnackbar('Error viewing row history', 'error');
    }
  };

  // Add event listeners for mouse move and up
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Add resizing class to container
      const container = document.querySelector('.excel-like-view-container');
      if (container) {
        container.classList.add('resizing');
      }
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        // Remove resizing class from container
        if (container) {
          container.classList.remove('resizing');
        }
      };
    }
  }, [isResizing, resizingColumn, startX, startWidth]);

  // Helper function to determine which columns to show based on media type
  const shouldShowColumn = (columnName) => {
    const mediaTypeLower = (mediaType || '').toLowerCase();
    
    switch (columnName) {
      case 'digital_vendor':
      case 'digital_ad_format':
        return mediaTypeLower === 'digital' || mediaTypeLower === 'digital amplify';
      case 'ooh_format':
        return mediaTypeLower === 'ooh';
      case 'asset_video_length_image_type':
        // Show for digital types
        return mediaTypeLower === 'digital' || mediaTypeLower === 'digital amplify';
      default:
        return true; // Show all other columns by default
    }
  };

  // Filter and sort data when filterValues, sortConfig, or accrualData changes
  useEffect(() => {
    let filteredResult = isEditMode && !isSnapshotView ? editedData : accrualData;

    // Apply filters
    if (Object.keys(filterValues).length > 0) {
      filteredResult = filteredResult.filter(row => {
        return Object.entries(filterValues).every(([columnKey, filterValue]) => {
          if (!filterValue || filterValue.trim() === '') return true;

          let cellValue = '';
          switch (columnKey) {
            case 'month':
              cellValue = row.month || '';
              break;
            case 'status':
              cellValue = row.status || '';
              break;
            case 'media':
              cellValue = row.media_type || '';
              break;
            case 'sub_media':
              cellValue = row.sub_media_type || '';
              break;
            case 'vendor':
              cellValue = row.digital_vendor || '';
              break;
            case 'format':
              cellValue = row.digital_ad_format || '';
              break;
            case 'region':
              cellValue = row.region || '';
              break;
            case 'ooh':
              cellValue = row.ooh_format || '';
              break;
            case 'asset':
              cellValue = row.asset_video_length_image_type || '';
              break;
            case 'given':
              cellValue = row.given_gross_cost ? row.given_gross_cost.toString() : '';
              break;
            case 'planning':
              cellValue = row.planning_gross_cost ? row.planning_gross_cost.toString() : '';
              break;
            case 'actual':
              cellValue = row.actual_gross_cost ? row.actual_gross_cost.toString() : '';
              break;
            case 'net_cost':
              cellValue = row.net_cost ? row.net_cost.toString() : '';
              break;
            case 'tracking':
              cellValue = row.tracking_cost ? row.tracking_cost.toString() : '';
              break;
            case 'tax':
              cellValue = row.tax ? row.tax.toString() : '';
              break;
            case 'coupon':
              cellValue = row.coupon ? row.coupon.toString() : '';
              break;
            case 'ooscope':
              cellValue = row.ooscope ? row.ooscope.toString() : '';
              break;
            case 'saving':
              cellValue = row.saving ? row.saving.toString() : '';
              break;
            case 'po':
              cellValue = row.po_number || '';
              break;
            case 'unused':
              cellValue = row.unused ? row.unused.toString() : '';
              break;
            case 'ntw':
              cellValue = row.national ? row.national.toString() : '';
              break;
            case 'north':
              cellValue = row.north ? row.north.toString() : '';
              break;
            case 'central':
              cellValue = row.central ? row.central.toString() : '';
              break;
            case 'rest':
              cellValue = row.rest ? row.rest.toString() : '';
              break;
            case 'ghcm':
              cellValue = row.ghcm ? row.ghcm.toString() : '';
              break;
            case 'mekong':
              cellValue = row.mekong ? row.mekong.toString() : '';
              break;
            default:
              cellValue = '';
          }

          return cellValue.toLowerCase().includes(filterValue.toLowerCase());
        });
      });
    }

    // Apply sorting
    if (sortConfig.column) {
      filteredResult = [...filteredResult].sort((a, b) => {
        let aValue = '';
        let bValue = '';

        switch (sortConfig.column) {
          case 'month':
            aValue = a.month || '';
            bValue = b.month || '';
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
          case 'media':
            aValue = a.media_type || '';
            bValue = b.media_type || '';
            break;
          case 'sub_media':
            aValue = a.sub_media_type || '';
            bValue = b.sub_media_type || '';
            break;
          case 'vendor':
            aValue = a.digital_vendor || '';
            bValue = b.digital_vendor || '';
            break;
          case 'format':
            aValue = a.digital_ad_format || '';
            bValue = b.digital_ad_format || '';
            break;
          case 'region':
            aValue = a.region || '';
            bValue = b.region || '';
            break;
          case 'ooh':
            aValue = a.ooh_format || '';
            bValue = b.ooh_format || '';
            break;
          case 'asset':
            aValue = a.asset_video_length_image_type || '';
            bValue = b.asset_video_length_image_type || '';
            break;
          case 'po':
            aValue = a.po_number || '';
            bValue = b.po_number || '';
            break;
          // Numeric fields
          case 'given':
          case 'planning':
          case 'actual':
          case 'net_cost':
          case 'tracking':
          case 'tax':
          case 'coupon':
          case 'ooscope':
          case 'saving':
          case 'unused':
          case 'ntw':
          case 'north':
          case 'central':
          case 'rest':
          case 'ghcm':
          case 'mekong':
            const getNumericValue = (row, field) => {
              switch (field) {
                case 'given': return parseFloat(row.given_gross_cost) || 0;
                case 'planning': return parseFloat(row.planning_gross_cost) || 0;
                case 'actual': return parseFloat(row.actual_gross_cost) || 0;
                case 'net_cost': return parseFloat(row.net_cost) || 0;
                case 'tracking': return parseFloat(row.tracking_cost) || 0;
                case 'tax': return parseFloat(row.tax) || 0;
                case 'coupon': return parseFloat(row.coupon) || 0;
                case 'ooscope': return parseFloat(row.ooscope) || 0;
                case 'saving': return parseFloat(row.saving) || 0;
                case 'unused': return parseFloat(row.unused) || 0;
                case 'ntw': return parseFloat(row.national) || 0;
                case 'north': return parseFloat(row.north) || 0;
                case 'central': return parseFloat(row.central) || 0;
                case 'rest': return parseFloat(row.rest) || 0;
                case 'ghcm': return parseFloat(row.ghcm) || 0;
                case 'mekong': return parseFloat(row.mekong) || 0;
                default: return 0;
              }
            };
            aValue = getNumericValue(a, sortConfig.column);
            bValue = getNumericValue(b, sortConfig.column);
            break;
          default:
            aValue = '';
            bValue = '';
        }

        // Handle numeric comparison
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        // Handle string comparison
        const aStr = aValue.toString().toLowerCase();
        const bStr = bValue.toString().toLowerCase();
        
        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredData(filteredResult);
  }, [filterValues, sortConfig, accrualData, editedData, isEditMode, isSnapshotView]);

  // Clear all filters function
  const clearAllFilters = () => {
    setFilterValues({});
    setSortConfig({ column: null, direction: 'asc' });
  };

  // Helper function to render header cell with or without filter
  const renderHeaderCell = (columnKey, title, align = 'center', sx = {}) => {
    const width = columnWidths[columnKey];
    
    // Columns that shouldn't have filters (action columns)
    const nonFilterableColumns = ['edit', 'data', 'check'];
    
    if (nonFilterableColumns.includes(columnKey)) {
      return renderResizableTableCell(columnKey, title, align, sx);
    }
    
    // Use FilterHeaderCell for filterable columns
    return <FilterHeaderCell columnKey={columnKey} title={title} align={align} width={width} />;
  };

  // Helper function to render resizable table headers
  const renderResizableTableCell = (columnKey, children, align = 'center', sx = {}) => {
    const width = columnWidths[columnKey];
    return (
      <TableCell 
        sx={{ 
          width: `${width}px`, 
          minWidth: `${width}px`,
          maxWidth: `${width}px`,
          position: 'relative',
          ...sx
        }}
        align={align}
      >
        {children}
        <Box
          className="column-resizer"
          onMouseDown={(e) => handleMouseDown(e, columnKey)}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '5px',
            height: '100%',
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.3)',
            },
          }}
        />
      </TableCell>
    );
  };

  // Helper function to render table body cells with consistent widths
  const renderTableBodyCell = (columnKey, children, align = 'left', sx = {}) => {
    const width = columnWidths[columnKey];
    return (
      <TableCell 
        sx={{ 
          width: `${width}px`, 
          minWidth: `${width}px`,
          maxWidth: `${width}px`,
          ...sx
        }}
        align={align}
      >
        {children}
      </TableCell>
    );
  };

  // Component for Status Field (Lock/Unlock only)
  const StatusField = ({ row, rowIndex, disabled }) => {
    const isLocked = row.is_locked;
    const currentUnlockStatus = rowUnlockStatus[row.id];
    const isRequestPending = currentUnlockStatus === 'pending';

    return (
      <FormControl fullWidth size="small" disabled={disabled}>
        <Select
          value={isLocked ? 'locked' : 'unlocked'}
          onChange={(e) => {
            if (e.target.value === 'unlocked' && isLocked) {
              // Request unlock
              handleLockToggle(row.id, true);
            } else if (e.target.value === 'locked' && !isLocked) {
              // Lock the row
              handleLockToggle(row.id, false);
            }
          }}
          variant="outlined"
          sx={{ fontSize: FONT_SIZES.body }}
        >
          <MenuItem value="locked">
            <Box display="flex" alignItems="center" gap={1}>
              🔒 Locked
              {isRequestPending && (
                <Chip 
                  size="small" 
                  label="Unlock Pending" 
                  color="warning" 
                  sx={{ fontSize: '0.6rem' }}
                />
              )}
            </Box>
          </MenuItem>
          <MenuItem value="unlocked">
            <Box display="flex" alignItems="center" gap={1}>
              🔓 Unlocked
            </Box>
          </MenuItem>
        </Select>
      </FormControl>
    );
  };

  // Filter Header Cell Component with search and sort functionality
  const FilterHeaderCell = ({ columnKey, title, align = 'center', width }) => {
    const currentSort = sortConfig.column === columnKey ? sortConfig.direction : null;
    const filterValue = filterValues[columnKey] || '';

    const handleFilterChange = (e) => {
      const value = e.target.value;
      setFilterValues(prev => ({
        ...prev,
        [columnKey]: value
      }));
    };

    const handleSort = () => {
      const newDirection = currentSort === 'asc' ? 'desc' : 'asc';
      setSortConfig({ column: columnKey, direction: newDirection });
    };

    const clearFilter = () => {
      setFilterValues(prev => {
        const newValues = { ...prev };
        delete newValues[columnKey];
        return newValues;
      });
    };

    return (
      <TableCell 
        sx={{ 
          width: `${width}px`, 
          minWidth: `${width}px`,
          maxWidth: `${width}px`,
          position: 'relative',
          padding: '8px 4px',
          backgroundColor: isSnapshotView ? '#fff3e0' : '#f8f9fa'
        }}
        align={align}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Header with title and sort button */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600,
                fontSize: FONT_SIZES.tableHeader,
                color: isSnapshotView ? '#e65100' : '#2e7d32',
                textAlign: 'center',
                lineHeight: 1.2
              }}
            >
              {title}
            </Typography>
            <IconButton 
              size="small" 
              onClick={handleSort}
              sx={{ 
                p: 0.2,
                color: currentSort ? (isSnapshotView ? '#e65100' : '#2e7d32') : 'rgba(0,0,0,0.4)'
              }}
            >
              {currentSort === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : 
               currentSort === 'desc' ? <ArrowDownwardIcon fontSize="small" /> : 
               <FilterListIcon fontSize="small" />}
            </IconButton>
          </Box>
          
          {/* Search input */}
          <TextField
            size="small"
            placeholder={`Filter ${title.toLowerCase()}...`}
            value={filterValue}
            onChange={handleFilterChange}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: '0.75rem',
                height: '28px',
                backgroundColor: 'white',
                '& input': {
                  padding: '4px 8px',
                  textAlign: align === 'center' ? 'center' : 'left'
                }
              },
              '& .MuiInputBase-input::placeholder': {
                fontSize: '0.7rem',
                opacity: 0.6
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.4)' }} />
                </InputAdornment>
              ),
              endAdornment: filterValue && (
                <InputAdornment position="end">
                  <IconButton 
                    size="small" 
                    onClick={clearFilter}
                    sx={{ p: 0.2 }}
                  >
                    <CloseIcon sx={{ fontSize: '0.7rem' }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>
        
        {/* Column resizer */}
        <Box
          className="column-resizer"
          onMouseDown={(e) => handleMouseDown(e, columnKey)}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '5px',
            height: '100%',
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.3)',
            },
          }}
        />
      </TableCell>
    );
  };

  return (
    <ExcelViewWrapper>
      <Container maxWidth={false} sx={{ 
        px: { xs: 1.5, sm: 2, md: 3 },
        width: '100%',
        maxWidth: 'none',
        margin: '0 auto',
        boxSizing: 'border-box'
      }}>
        <Box 
          className="excel-like-view-container" 
          sx={{ 
            width: '100%', 
            mt: { xs: 0.5, md: 1 }, 
            mb: 1,
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}
       >
      {/* Header with View Type Indicator */}
      <Paper elevation={0} sx={{ 
        mb: { xs: 1.5, sm: 2, md: 2.5 },
        p: { xs: 2, sm: 2.5, md: 3 },
        background: isSnapshotView 
          ? 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 50%, #ffe082 100%)' 
          : 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 50%, #388e3c 100%)',
        color: isSnapshotView ? '#e65100' : 'white',
        position: 'relative',
        overflow: 'hidden',
        border: isSnapshotView ? '2px solid #ff9800' : '2px solid #4caf50',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isSnapshotView 
            ? 'rgba(255, 152, 0, 0.1)' 
            : 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          zIndex: 0
        }
      }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            maxWidth: '100%',
            flexWrap: 'wrap',
            gap: 1
          }}>
                      <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 1, sm: 2 }, 
              flex: 1, 
              minWidth: 0,
              maxWidth: { xs: '100%', md: '60%' }
            }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                if (snapshotMonth) {
                  navigate(`/campaigns?snapshotMonth=${snapshotMonth}`);
                } else {
                  navigate('/campaigns');
                }
              }}
              sx={{ 
                color: isSnapshotView ? '#856404' : 'white',
                borderColor: isSnapshotView ? 'rgba(133, 100, 4, 0.3)' : 'rgba(255,255,255,0.3)',
                borderRadius: 2,
                fontWeight: 500,
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.5, sm: 0.8 },
                minWidth: 'auto',
                '&:hover': {
                  borderColor: isSnapshotView ? '#856404' : 'white',
                  backgroundColor: isSnapshotView ? 'rgba(133, 100, 4, 0.1)' : 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Back to Campaigns
            </Button>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                {isSnapshotView ? '📊 Snapshot View' : '💻 Current Data View'}
              </Typography>
              {campaign && (
                <Typography variant="body2" sx={{ 
                  opacity: 0.9, 
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  fontWeight: 500,
                  mb: 0.5
                }}>
                  {campaign.brand_name && `${campaign.brand_name} - `}
                  {campaign.campaign_name || campaign.name || `Campaign ${campaignId}`} ({mediaType})
                </Typography>
              )}
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                {isSnapshotView 
                  ? `Viewing historical data from ${snapshotMonth} (Read-only)`
                  : 'Live data - can be edited and modified'
                }
              </Typography>
              {isSnapshotView && (
                <Box sx={{ mt: 2 }}>
                  <Chip 
                    label="READ ONLY" 
                    sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontWeight: 'bold'
                    }} 
                  />
                </Box>
              )}
            </Box>
          </Box>
          {!isSnapshotView && (
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 1.5 }, 
              alignItems: 'center', 
              flexShrink: 0, 
              flexWrap: 'wrap',
              maxWidth: { xs: '100%', md: '40%' },
              justifyContent: 'flex-end'
            }}>
              {/* Version Management */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 100, maxWidth: 140 }}>
                  <InputLabel sx={{ color: 'white', '&.Mui-focused': { color: 'white' } }}>Version</InputLabel>
                  <Select
                    value={selectedVersion || 'current'}
                    label="Version"
                    onChange={e => handleSelectVersion(e.target.value)}
                    sx={{ 
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.5)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'white',
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'white',
                      }
                    }}
                  >
                    <MenuItem value="current">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          backgroundColor: '#4caf50' 
                        }} />
                        Current (Live Data)
                      </Box>
                    </MenuItem>
                    {versions.map(v => (
                      <MenuItem key={v.id} value={v.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            backgroundColor: '#ff9800' 
                          }} />
                          {v.label} ({new Date(v.created_at).toLocaleDateString()})
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {isPreview && selectedVersion && (
                  <Button 
                    variant="outlined" 
                    color="inherit" 
                    onClick={handleBackToLive} 
                    size="small"
                    sx={{ 
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.3)',
                      fontSize: '0.75rem',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    Back to Live
                  </Button>
                )}
              </Box>

              {/* Edit Mode Controls */}
              {isEditMode && (
                <Chip 
                  label="EDITING" 
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    display: { xs: 'none', sm: 'flex' }
                  }} 
                />
              )}
              {canEditAccruals && (
                <Button
                  variant="contained"
                  onClick={toggleEditMode}
                  startIcon={isEditMode ? <CloseIcon /> : <EditIcon />}
                  sx={{ 
                    backgroundColor: isEditMode ? '#d32f2f' : 'rgba(255,255,255,0.15)',
                    color: 'white',
                    borderRadius: 2,
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    px: { xs: 1.5, sm: 2.5 },
                    py: { xs: 0.8, sm: 1 },
                    minWidth: 'auto',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      backgroundColor: isEditMode ? '#b71c1c' : 'rgba(255,255,255,0.25)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    }
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {isEditMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    {isEditMode ? 'Exit' : 'Edit'}
                  </Box>
                </Button>
              )}
              
              {/* Clear All Filters Button */}
              {Object.keys(filterValues).length > 0 || sortConfig.column && (
                <Button
                  variant="outlined"
                  onClick={clearAllFilters}
                  startIcon={<CloseIcon />}
                  sx={{ 
                    borderColor: isSnapshotView ? '#ff9800' : 'rgba(255,255,255,0.3)',
                    color: isSnapshotView ? '#e65100' : 'white',
                    borderRadius: 2,
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    px: { xs: 1.5, sm: 2.5 },
                    py: { xs: 0.8, sm: 1 },
                    minWidth: 'auto',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      borderColor: isSnapshotView ? '#f57c00' : 'rgba(255,255,255,0.5)',
                      backgroundColor: isSnapshotView ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255,255,255,0.1)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    }
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Clear Filters
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    Clear
                  </Box>
                </Button>
              )}
            </Box>
          )}
          </Box>
        </Box>
      </Paper>



      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Campaign Info */}
          {campaign && (
            <Paper sx={{ 
              p: { xs: 1, sm: 1.5 }, 
              mb: { xs: 1, sm: 1.5 },
              borderRadius: 2,
              backgroundColor: 'white',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
            }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Brand / Sub-brand
                  </Typography>
                  <Typography variant="body1">
                    {campaign.brand_name}
                    {campaign.sub_brand_name && ` / ${campaign.sub_brand_name}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Period
                  </Typography>
                  <Typography variant="body1">
                    {campaign.start_date && campaign.end_date
                      ? `${format(new Date(campaign.start_date), 'dd/MM/yyyy')} - ${format(new Date(campaign.end_date), 'dd/MM/yyyy')}`
                      : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Status
                  </Typography>
                  <Chip
                    label={determineCampaignStatus(campaign)}
                    sx={{
                      backgroundColor: 
                        determineCampaignStatus(campaign) === 'DONE'
                          ? '#9e9e9e' // Grey for DONE
                          : determineCampaignStatus(campaign) === 'ON-GOING'
                          ? '#4caf50' // Green for ON-GOING
                          : '#ffc107', // Yellow for PLANNING
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Version Comparison Banner */}
          {isPreview && selectedVersion && (
            <Paper sx={{ 
              p: 2, 
              mb: 2,
              backgroundColor: '#fff3e0',
              border: '1px solid #ffb74d',
              borderRadius: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '50%', 
                  backgroundColor: '#ff9800',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.2rem'
                }}>
                  🔍
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ color: '#e65100', fontWeight: 'bold', mb: 0.5 }}>
                    Version Comparison Mode
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#bf360c' }}>
                    Viewing version comparison. Cells with changes are highlighted in{' '}
                    <Box component="span" sx={{ 
                      backgroundColor: '#ffebee', 
                      padding: '2px 6px', 
                      borderRadius: 1,
                      border: '1px solid #f44336'
                    }}>
                      light red
                    </Box>
                    {' '}with a red dot indicator. Hover over highlighted cells to see what changed.
                  </Typography>
                </Box>
                <Button 
                  variant="outlined" 
                  onClick={handleBackToLive}
                  sx={{ 
                    color: '#e65100',
                    borderColor: '#e65100',
                    '&:hover': {
                      backgroundColor: 'rgba(230, 81, 0, 0.1)',
                      borderColor: '#bf360c'
                    }
                  }}
                >
                  Exit Comparison
                </Button>
              </Box>
            </Paper>
          )}

          {/* Excel-like Data Table */}
          <Paper sx={{ 
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid rgba(76, 175, 80, 0.2)',
            backgroundColor: 'background.paper',
            mb: { xs: 1, sm: 1.5 },
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            <Box sx={{ 
              p: { xs: 0.5, sm: 1 },
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              background: isEditMode && !isSnapshotView 
                ? '#e8f5e8'
                : isSnapshotView
                ? '#fff3e0'
                : '#f8f9fa',
              borderBottom: '1px solid',
              borderColor: isEditMode && !isSnapshotView 
                ? 'rgba(76, 175, 80, 0.3)'
                : isSnapshotView
                ? 'rgba(255, 152, 0, 0.3)'
                : 'rgba(0, 0, 0, 0.08)'
            }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom sx={{ 
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  color: isSnapshotView ? '#e65100' : 'inherit'
                }}>
                  {isSnapshotView ? '📸 Snapshot Data' : '📋 Detail Data'}
                </Typography>
                <Typography variant="caption" sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  color: isSnapshotView ? '#f57700' : 'textSecondary'
                }}>
                  {isEditMode && !isSnapshotView 
                    ? 'Click to edit. Use delete button to remove rows.'
                    : isSnapshotView 
                    ? `📅 Historical data from ${snapshotMonth} - read-only`
                    : 'Live data view - switch to edit mode to make changes'
                  }
                </Typography>
              </Box>
              {isEditMode && !isSnapshotView && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="success.main" fontWeight="bold">
                    ✏️ EDIT MODE ACTIVE
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Divider />
            
            <div className="table-box">
              <TableContainer component={Paper} sx={{ 
                maxHeight: '100%', 
                overflow: 'auto',
                width: '100%',
                height: '100%',
                borderRadius: '8px',
                border: 'none',
                boxShadow: 'none'
              }}>
              <Table size="small" sx={{ 
                backgroundColor: 'white',
                width: 'auto',
                minWidth: 'max-content',
                tableLayout: 'auto',
                '& .MuiTableCell-root': {
                  fontSize: FONT_SIZES.table,
                  borderColor: 'rgba(0, 0, 0, 0.08)',
                  py: { xs: 0.5, sm: 0.75 },
                  px: { xs: 0.75, sm: 1 },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.2,
                  borderRight: '1px solid rgba(0, 0, 0, 0.06)',
                  color: '#333333',
                  '&:last-child': {
                    borderRight: 'none'
                  }
                },
                '& .MuiTableCell-head': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  fontWeight: 600
                },
                '& .MuiTableRow-root:nth-of-type(even)': {
                  backgroundColor: '#fafafa'
                },
                '& .MuiTableRow-root:hover': {
                  backgroundColor: '#f0f7ff !important'
                }
              }}>
                <TableHead>
                  <TableRow sx={{ 
                    backgroundColor: isSnapshotView ? '#fff3e0' : '#f8f9fa',
                    '& .MuiTableCell-head': {
                      color: isSnapshotView ? '#e65100' : '#2e7d32',
                      fontWeight: 600,
                      fontSize: FONT_SIZES.tableHeader,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                      backgroundColor: isSnapshotView ? '#fff3e0' : '#f8f9fa',
                      py: { xs: 0.75, sm: 1 },
                      px: { xs: 0.5, sm: 0.75 },
                      textAlign: 'center'
                    }
                  }}>
                    {isEditMode && !isSnapshotView && renderHeaderCell('edit', 'Edit')}
                    {!isSnapshotView && renderHeaderCell('data', 'Data')}
                    {renderHeaderCell('month', 'Month')}
                    {renderHeaderCell('monthly_status', 'Monthly Status')}
                    {renderHeaderCell('status', 'Status')}
                    {renderHeaderCell('media', 'Media')}
                    {renderHeaderCell('sub_media', 'Sub Media')}
                    {shouldShowColumn('digital_vendor') && renderHeaderCell('vendor', 'Vendor')}
                    {shouldShowColumn('digital_ad_format') && renderHeaderCell('format', 'Format')}
                    {renderHeaderCell('region', 'Region')}
                    {renderHeaderCell('budget_format', 'Budget Format')}
                    {shouldShowColumn('ooh_format') && renderHeaderCell('ooh', 'OOH')}
                    {shouldShowColumn('asset_video_length_image_type') && renderHeaderCell('asset', 'Asset')}
                    {renderHeaderCell('given', 'Given', 'right')}
                    {renderHeaderCell('planning', 'Planning', 'right')}
                    {renderHeaderCell('actual', 'Actual', 'right')}
                    {renderHeaderCell('net_cost', 'Net Cost', 'right')}
                    {renderHeaderCell('tracking', 'Tracking', 'right')}
                    {renderHeaderCell('tax', 'Tax', 'right')}
                    {renderHeaderCell('coupon', 'Coupon', 'right')}
                    {renderHeaderCell('ooscope', 'OOScope', 'right')}
                    {renderHeaderCell('saving', 'Saving', 'right')}
                    {renderHeaderCell('po', 'PO')}
                    {renderHeaderCell('unused', 'Unused', 'right')}
                    {renderHeaderCell('ntw', 'NTW', 'right')}
                    {renderHeaderCell('north', 'North', 'right')}
                    {renderHeaderCell('central', 'Central', 'right')}
                    {renderHeaderCell('rest', 'Rest', 'right')}
                    {renderHeaderCell('ghcm', 'GHCM', 'right')}
                    {renderHeaderCell('mekong', 'Mekong', 'right')}
                    {renderHeaderCell('check', 'Check', 'center')}
                  </TableRow>
                </TableHead>
                
                <TableBody>
                  {/* Use filtered data for display */}
                  {filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={
                        25 + // Base columns (including new monthly_status, sub_media_type and budget_format columns)
                        (shouldShowColumn('digital_vendor') ? 1 : 0) +
                        (shouldShowColumn('digital_ad_format') ? 1 : 0) +
                        (shouldShowColumn('ooh_format') ? 1 : 0) +
                        (shouldShowColumn('asset_video_length_image_type') ? 1 : 0) +
                        (isEditMode && !isSnapshotView ? 1 : 0) + // Edit Actions column
                        (!isSnapshotView ? 1 : 0) // Data Actions column
                      } align="center">
                        <Typography color="textSecondary" sx={{ py: 4 }}>
                          No data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    isEditMode && !isSnapshotView ? (
                        filteredData.map((row, rowIndex) => (
                          <TableRow 
                            key={row.id || rowIndex} 
                            hover
                            sx={{ 
                              backgroundColor: row.is_locked 
                                ? (rowIndex % 2 === 0 ? '#ffeaea' : '#fff5f5')
                                : (rowIndex % 2 === 0 ? '#f0f9f0' : 'white'),
                              border: row.is_locked 
                                ? '1px solid rgba(211, 47, 47, 0.2)'
                                : '1px solid rgba(76, 175, 80, 0.1)',
                              opacity: row.is_locked ? 0.7 : 1,
                              '&:hover': { 
                                backgroundColor: row.is_locked 
                                  ? '#ffebee !important'
                                  : '#e8f5e8 !important',
                                boxShadow: row.is_locked 
                                  ? '0 2px 8px rgba(211, 47, 47, 0.15)'
                                  : '0 2px 8px rgba(76, 175, 80, 0.15)'
                              }
                            }}
                          >
                            <TableCell sx={{ width: '60px', minWidth: '60px', textAlign: 'center' }}>
                              {row.is_locked ? (
                                <Chip 
                                  label="🔒" 
                                  size="small" 
                                  color="error" 
                                  variant="filled"
                                  sx={{ 
                                    fontSize: '0.7rem',
                                    height: '28px',
                                    cursor: 'not-allowed'
                                  }}
                                />
                              ) : (
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleDeleteRow(rowIndex)}
                                  sx={{ 
                                    color: '#d32f2f',
                                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                    border: '1px solid rgba(211, 47, 47, 0.2)',
                                    borderRadius: 1,
                                    '&:hover': { 
                                      backgroundColor: 'rgba(211, 47, 47, 0.15)',
                                      borderColor: 'rgba(211, 47, 47, 0.3)'
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell sx={{ width: '80px', minWidth: '80px', textAlign: 'center' }}>
                              <IconButton 
                                size="small" 
                                onClick={(e) => handleDataMenuOpen(e, row)}
                                sx={{ 
                                  color: '#666',
                                  '&:hover': { 
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                  }
                                }}
                                title="Options"
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ width: '70px', minWidth: '70px' }}>
                              <EditFormField
                                column={{ type: 'text' }}
                                value={row.month}
                                onChange={(value) => handleFieldChange(rowIndex, 'month', value)}
                                fieldName="month"
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell sx={{ width: '120px', minWidth: '120px' }}>
                              <EditFormField
                                column={{ 
                                  type: 'select',
                                  options: [
                                    { value: 'planning', label: 'Planning' },
                                    { value: 'actual', label: 'Actual' }
                                  ]
                                }}
                                value={row.monthly_status || 'planning'}
                                onChange={(value) => handleFieldChange(rowIndex, 'monthly_status', value)}
                                fieldName="monthly_status"
                                campaign={campaign}
                                dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell sx={{ width: '120px', minWidth: '120px' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <StatusField
                                row={row}
                                rowIndex={rowIndex}
                                disabled={row.is_locked}
                              />
                                {row.is_locked && rowUnlockStatus[row.id] === 'pending' && (
                                  <Chip 
                                    size="small" 
                                    label="Pending" 
                                    color="warning" 
                                    sx={{ fontSize: '0.6rem' }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ width: '80px', minWidth: '80px' }}>
                              <EditFormField
                                column={{ type: 'text' }}
                                value={row.media_type}
                                onChange={(value) => handleFieldChange(rowIndex, 'media_type', value)}
                                fieldName="media_type"
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'text' }}
                                value={row.sub_media_type}
                                onChange={(value) => handleFieldChange(rowIndex, 'sub_media_type', value)}
                                fieldName="sub_media_type"
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            {shouldShowColumn('digital_vendor') && (
                              <TableCell sx={{ minWidth: '150px' }}>
                                <EditFormField
                                  column={{ type: 'text' }}
                                  value={row.digital_vendor}
                                  onChange={(value) => handleFieldChange(rowIndex, 'digital_vendor', value)}
                                  fieldName="digital_vendor"
                                  vendors={vendors}
                                  digitalAdFormats={digitalAdFormats}
                                  oohFormats={oohFormats}
                                  campaign={campaign}
                                  dimensionData={dimensionData}
                                  disabled={row.is_locked}
                                />
                              </TableCell>
                            )}
                            {shouldShowColumn('digital_ad_format') && (
                              <TableCell sx={{ minWidth: '150px' }}>
                                <EditFormField
                                  column={{ type: 'text' }}
                                  value={row.digital_ad_format}
                                  onChange={(value) => handleFieldChange(rowIndex, 'digital_ad_format', value)}
                                  fieldName="digital_ad_format"
                                  vendors={vendors}
                                  digitalAdFormats={digitalAdFormats}
                                  oohFormats={oohFormats}
                                  campaign={campaign}
                                  dimensionData={dimensionData}
                                  disabled={row.is_locked}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'text' }}
                                value={row.region_focus}
                                onChange={(value) => handleFieldChange(rowIndex, 'region_focus', value)}
                                fieldName="region_focus"
                                vendors={vendors}
                                digitalAdFormats={digitalAdFormats}
                                oohFormats={oohFormats}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: '130px' }}>
                              <EditFormField
                                column={{ type: 'select' }}
                                value={row.budget_format}
                                onChange={(value) => handleFieldChange(rowIndex, 'budget_format', value)}
                                fieldName="budget_format"
                                campaign={campaign}
                                dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            {shouldShowColumn('ooh_format') && (
                              <TableCell sx={{ minWidth: '120px' }}>
                                <EditFormField
                                  column={{ type: 'text' }}
                                  value={row.ooh_format}
                                  onChange={(value) => handleFieldChange(rowIndex, 'ooh_format', value)}
                                  fieldName="ooh_format"
                                  vendors={vendors}
                                  digitalAdFormats={digitalAdFormats}
                                  oohFormats={oohFormats}
                                  campaign={campaign}
                                  dimensionData={dimensionData}
                                  disabled={row.is_locked}
                                />
                              </TableCell>
                            )}
                            {shouldShowColumn('asset_video_length_image_type') && (
                              <TableCell sx={{ minWidth: '180px' }}>
                                <EditFormField
                                  column={{ type: 'text' }}
                                  value={row.asset_video_length_image_type}
                                  onChange={(value) => handleFieldChange(rowIndex, 'asset_video_length_image_type', value)}
                                  fieldName="asset_video_length_image_type"
                                  campaign={campaign}
                                  dimensionData={dimensionData}
                                  disabled={row.is_locked}
                                />
                              </TableCell>
                            )}
                            <TableCell align="right" sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'currency' }}
                                value={row.given}
                                onChange={(value) => handleFieldChange(rowIndex, 'given', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked || !canEditField('given')}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '180px' }}>
                              <EditFormField
                                column={{ type: 'currency' }}
                                value={row.planning_gross_cost}
                                onChange={(value) => handleFieldChange(rowIndex, 'planning_gross_cost', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked || !canEditField('planning_gross_cost')}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '180px' }}>
                              <Box sx={{ 
                                padding: '8px 12px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontWeight: 500,
                                color: '#666',
                                fontSize: FONT_SIZES.body
                              }}>
                                {formatCurrency(row.actual_gross_cost)}
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'currency' }}
                                value={row.net_net_cost}
                                onChange={(value) => handleFieldChange(rowIndex, 'net_net_cost', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'currency' }}
                                value={row.tracking_fees}
                                onChange={(value) => handleFieldChange(rowIndex, 'tracking_fees', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'currency' }}
                                value={row.whtax}
                                onChange={(value) => handleFieldChange(rowIndex, 'whtax', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '180px' }}>
                              <EditFormField
                                column={{ type: 'currency' }}
                                value={row.coupon}
                                onChange={(value) => handleFieldChange(rowIndex, 'coupon', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'currency' }}
                                value={row.ooscope_fees}
                                onChange={(value) => handleFieldChange(rowIndex, 'ooscope_fees', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '180px' }}>
                              <Box sx={{ 
                                padding: '8px 12px',
                                backgroundColor: '#f0f8ff',
                                borderRadius: '4px',
                                border: '1px solid #cce7ff',
                                fontWeight: 500,
                                color: '#1976d2',
                                fontSize: FONT_SIZES.body,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: '2px'
                              }}>
                                <Box>{row.savingDisplay ? row.savingDisplay : formatCurrency(row.saving)}</Box>
                                <Box sx={{ 
                                  fontSize: '0.7rem',
                                  ...row.savingPercentage?.style
                                }}>
                                  {row.savingPercentage?.display || 'NA'}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'text' }}
                                value={row.po_number}
                                onChange={(value) => handleFieldChange(rowIndex, 'po_number', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '180px' }}>
                              <Box sx={{ 
                                padding: '8px 12px',
                                backgroundColor: '#fff8e1',
                                borderRadius: '4px',
                                border: '1px solid #ffcc02',
                                fontWeight: 500,
                                color: '#f57c00',
                                fontSize: FONT_SIZES.body
                              }}>
                                {formatCurrency(row.unused)}
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'currency' }}
                                value={row.ntw}
                                onChange={(value) => handleFieldChange(rowIndex, 'ntw', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'currency' }}
                                value={row.north}
                                onChange={(value) => handleFieldChange(rowIndex, 'north', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'currency' }}
                                value={row.central_mid}
                                onChange={(value) => handleFieldChange(rowIndex, 'central_mid', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'currency' }}
                                value={row.the_rest_of_central}
                                onChange={(value) => handleFieldChange(rowIndex, 'the_rest_of_central', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'currency' }}
                                value={row.ghcm}
                                onChange={(value) => handleFieldChange(rowIndex, 'ghcm', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: '150px' }}>
                              <EditFormField
                                column={{ type: 'currency' }}
                                value={row.mekong}
                                onChange={(value) => handleFieldChange(rowIndex, 'mekong', value)}
                                campaign={campaign}
                                  dimensionData={dimensionData}
                                disabled={row.is_locked}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ minWidth: '180px' }}>
                              <Box sx={{ 
                                padding: '4px 8px',
                                ...row.check?.style,
                                fontWeight: 500,
                                fontSize: FONT_SIZES.body,
                                textAlign: 'center'
                              }}>
                                {row.check?.display || '-'}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        filteredData.map((row, rowIndex) => (
                          <TableRow 
                            key={row.id || rowIndex} 
                            hover
                            sx={{ 
                              backgroundColor: isSnapshotView 
                                ? (rowIndex % 2 === 0 ? '#fff8e1' : '#fffde7')
                                : (rowIndex % 2 === 0 ? '#f9f9f9' : 'white'),
                              '&:hover': { 
                                backgroundColor: isSnapshotView ? '#ffecb3' : '#f5f5f5' 
                              }
                            }}
                          >
                                                          {!isSnapshotView && (
                                <TableCell sx={{ minWidth: '60px', textAlign: 'center' }}>
                                                                <IconButton 
                                size="small" 
                                onClick={(e) => handleDataMenuOpen(e, row)}
                                sx={{ 
                                  color: '#666',
                                  '&:hover': { 
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                  }
                                }}
                                title="Options"
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                              </TableCell>
                            )}
                            <DiffTableCell row={row} fieldName="month" sx={{ minWidth: '120px' }}>{row.month ? format(new Date(row.month), 'MMM yyyy') : '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="monthly_status" sx={{ minWidth: '150px' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip 
                                  label={row.monthly_status || 'planning'} 
                                  size="small" 
                                  color={row.monthly_status === 'actual' ? 'success' : 'default'}
                                  variant="outlined"
                                  sx={{ 
                                    fontSize: '0.7rem',
                                    fontWeight: 500
                                  }}
                                />
                              </Box>
                            </DiffTableCell>
                            <DiffTableCell row={row} fieldName="status" sx={{ minWidth: '150px' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {row.is_locked ? '🔒 Locked' : '🔓 Unlocked'}
                                {row.is_locked && rowUnlockStatus[row.id] === 'pending' && (
                                  <Chip 
                                    size="small" 
                                    label="Unlock Pending" 
                                    color="warning" 
                                    sx={{ fontSize: '0.6rem' }}
                                  />
                                )}
                              </Box>
                            </DiffTableCell>
                            <DiffTableCell row={row} fieldName="media_type" sx={{ minWidth: '150px' }}>{row.media_type || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="sub_media_type" sx={{ minWidth: '150px' }}>{row.sub_media_type || '-'}</DiffTableCell>
                            {shouldShowColumn('digital_vendor') && <DiffTableCell row={row} fieldName="digital_vendor" sx={{ minWidth: '150px' }}>{row.digital_vendor || '-'}</DiffTableCell>}
                            {shouldShowColumn('digital_ad_format') && <DiffTableCell row={row} fieldName="digital_ad_format" sx={{ minWidth: '150px' }}>{row.digital_ad_format || '-'}</DiffTableCell>}
                            <DiffTableCell row={row} fieldName="region_focus" sx={{ minWidth: '150px' }}>{row.region_focus || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="budget_format" sx={{ minWidth: '130px' }}>{row.budget_format || '-'}</DiffTableCell>
                            {shouldShowColumn('ooh_format') && <DiffTableCell row={row} fieldName="ooh_format" sx={{ minWidth: '120px' }}>{row.ooh_format || '-'}</DiffTableCell>}
                            {shouldShowColumn('asset_video_length_image_type') && <DiffTableCell row={row} fieldName="asset_video_length_image_type" sx={{ minWidth: '180px' }}>{row.asset_video_length_image_type || '-'}</DiffTableCell>}
                            <DiffTableCell row={row} fieldName="given" align="right" sx={{ minWidth: '150px' }}>{formatCurrency(row.given) || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="planning_gross_cost" align="right" sx={{ minWidth: '180px' }}>{formatCurrency(row.planning_gross_cost) || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="actual_gross_cost" align="right" sx={{ minWidth: '180px' }}>
                              <Box sx={{ 
                                display: 'inline-block',
                                padding: '4px 8px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                fontWeight: 500,
                                color: '#666'
                              }}>
                                {formatCurrency(row.actual_gross_cost) || '-'}
                              </Box>
                            </DiffTableCell>
                            <DiffTableCell row={row} fieldName="net_net_cost" align="right" sx={{ minWidth: '150px' }}>{formatCurrency(row.net_net_cost) || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="tracking_fees" align="right" sx={{ minWidth: '150px' }}>{formatCurrency(row.tracking_fees) || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="whtax" align="right" sx={{ minWidth: '150px' }}>{formatCurrency(row.whtax) || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="coupon" align="right" sx={{ minWidth: '180px' }}>{formatCurrency(row.coupon) || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="ooscope_fees" align="right" sx={{ minWidth: '150px' }}>{formatCurrency(row.ooscope_fees) || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="saving" align="right" sx={{ minWidth: '180px' }}>
                              <Box sx={{ 
                                display: 'inline-flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                padding: '4px 8px',
                                backgroundColor: '#f0f8ff',
                                borderRadius: '4px',
                                fontWeight: 500,
                                color: '#1976d2',
                                gap: '2px'
                              }}>
                                <Box>{(() => {
                                  // Show saving when monthly_status = 'actual'; allow actual 0
                                  if (row.monthly_status === 'actual') {
                                    const planning = Number(row.planning_gross_cost) || 0;
                                    const actual = Number(row.actual_gross_cost) || 0;
                                    return formatCurrency(planning - actual);
                                  }
                                  return 'NA';
                                })()}</Box>
                                <Box sx={{ 
                                  fontSize: '0.7rem',
                                  ...row.savingPercentage?.style
                                }}>
                                  {(() => {
                                    if (row.monthly_status === 'actual') {
                                      const planning = Number(row.planning_gross_cost) || 0;
                                      const actual = Number(row.actual_gross_cost) || 0;
                                      const saving = planning - actual;
                                      const pct = planning > 0 ? (saving / planning) * 100 : 0;
                                      return `${pct.toFixed(1)}%`;
                                    }
                                    return 'NA';
                                  })()}
                                </Box>
                              </Box>
                            </DiffTableCell>
                            <DiffTableCell row={row} fieldName="po_number" sx={{ minWidth: '150px' }}>{row.po_number || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="unused" align="right" sx={{ minWidth: '180px' }}>
                              <Box sx={{ 
                                display: 'inline-block',
                                padding: '4px 8px',
                                backgroundColor: '#fff8e1',
                                borderRadius: '4px',
                                fontWeight: 500,
                                color: '#f57c00'
                              }}>
                                {formatCurrency(row.unused) || '-'}
                              </Box>
                            </DiffTableCell>
                            <DiffTableCell row={row} fieldName="ntw" align="right" sx={{ minWidth: '150px' }}>{formatCurrency(row.ntw) || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="north" align="right" sx={{ minWidth: '150px' }}>{formatCurrency(row.north) || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="central_mid" align="right" sx={{ minWidth: '150px' }}>{formatCurrency(row.central_mid) || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="the_rest_of_central" align="right" sx={{ minWidth: '150px' }}>{formatCurrency(row.the_rest_of_central) || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="ghcm" align="right" sx={{ minWidth: '150px' }}>{formatCurrency(row.ghcm) || '-'}</DiffTableCell>
                            <DiffTableCell row={row} fieldName="mekong" align="right" sx={{ minWidth: '150px' }}>{formatCurrency(row.mekong) || '-'}</DiffTableCell>
                            <TableCell align="center" sx={{ minWidth: '180px' }}>
                              <Box sx={{ 
                                padding: '4px 8px',
                                ...row.check?.style,
                                fontWeight: 500,
                                fontSize: FONT_SIZES.body,
                                textAlign: 'center'
                              }}>
                                {row.check?.display || '-'}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )
                  )}
                  {accrualData.length > 0 && (
                        <TableRow sx={{ 
                          backgroundColor: '#f8f9fa',
                          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                          '& .MuiTableCell-root': {
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            color: '#333333'
                          }
                        }}>
                          {isEditMode && !isSnapshotView && renderTableBodyCell('edit', '')}
                          {!isSnapshotView && renderTableBodyCell('data', '')}
                          {renderTableBodyCell('month', 
                            <Chip 
                              label="📊 TOTAL" 
                              size="small" 
                              color="primary" 
                              variant="filled"
                              sx={{ 
                                fontWeight: 'bold',
                                fontSize: '0.7rem'
                              }}
                            />
                          )}
                          {renderTableBodyCell('monthly_status', '')}
                          {renderTableBodyCell('status', '')}
                          {renderTableBodyCell('media', '')}
                          {renderTableBodyCell('sub_media', '')}
                          {shouldShowColumn('digital_vendor') && renderTableBodyCell('vendor', '')}
                          {shouldShowColumn('digital_ad_format') && renderTableBodyCell('format', '')}
                          {renderTableBodyCell('region', '')}
                          {renderTableBodyCell('budget_format', '')}
                          {shouldShowColumn('ooh_format') && renderTableBodyCell('ooh', '')}
                          {shouldShowColumn('asset_video_length_image_type') && renderTableBodyCell('asset', '')}
                          {renderTableBodyCell('given', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.given) || 0), 0)), 'right')}
                          {renderTableBodyCell('planning', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.planning_gross_cost) || 0), 0)), 'right')}
                          {renderTableBodyCell('actual', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.actual_gross_cost) || 0), 0)), 'right')}
                          {renderTableBodyCell('net_cost', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.net_net_cost) || 0), 0)), 'right')}
                          {renderTableBodyCell('tracking', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.tracking_fees) || 0), 0)), 'right')}
                          {renderTableBodyCell('tax', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.whtax) || 0), 0)), 'right')}
                          {renderTableBodyCell('coupon', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.coupon) || 0), 0)), 'right')}
                          {renderTableBodyCell('ooscope', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.ooscope_fees) || 0), 0)), 'right')}
                          {renderTableBodyCell('saving', 
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                              <Box>{(() => {
                                // Total saving only from rows marked actual; allow actual 0
                                const totalSaving = accrualData.reduce((sum, r) => {
                                  if (r.monthly_status === 'actual') {
                                    const planning = Number(r.planning_gross_cost) || 0;
                                    const actual = Number(r.actual_gross_cost) || 0;
                                    return sum + (planning - actual);
                                  }
                                  return sum;
                                }, 0);
                                return formatCurrency(totalSaving);
                              })()}</Box>
                              <Box sx={{ fontSize: '0.7rem', color: '#666' }}>
                                {(() => {
                                  const totalPlanning = accrualData.reduce((sum, r) => sum + (Number(r.planning_gross_cost) || 0), 0);
                                  const totalSaving = accrualData.reduce((sum, r) => {
                                    if (r.monthly_status === 'actual') {
                                      const planning = Number(r.planning_gross_cost) || 0;
                                      const actual = Number(r.actual_gross_cost) || 0;
                                      return sum + (planning - actual);
                                    }
                                    return sum;
                                  }, 0);
                                  const totalPercentage = totalPlanning > 0 ? (totalSaving / totalPlanning) * 100 : 0;
                                  return `${totalPercentage.toFixed(1)}%`;
                                })()}
                              </Box>
                            </Box>
                          , 'right')}
                          {renderTableBodyCell('po', '')} {/* PO Number total */}
                          {renderTableBodyCell('unused', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.unused) || 0), 0)), 'right')}
                          {renderTableBodyCell('ntw', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.ntw) || 0), 0)), 'right')}
                          {renderTableBodyCell('north', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.north) || 0), 0)), 'right')}
                          {renderTableBodyCell('central', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.central_mid) || 0), 0)), 'right')}
                          {renderTableBodyCell('rest', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.the_rest_of_central) || 0), 0)), 'right')}
                          {renderTableBodyCell('ghcm', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.ghcm) || 0), 0)), 'right')}
                          {renderTableBodyCell('mekong', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.mekong) || 0), 0)), 'right')}
                          {renderTableBodyCell('check', '', 'center')}
                        </TableRow>
                      )}
                </TableBody>
                </Table>
              </TableContainer>
            </div>
            </Paper>
          </>
        )}
      {/* Action buttons for edit mode */}
      {isEditMode && !isSnapshotView && (
        <Box sx={{ 
          position: 'sticky', 
          bottom: 20, 
          zIndex: 1000, 
          display: 'flex', 
          justifyContent: 'center',
          gap: 2,
          mt: 3,
          mb: 2 
        }}>
          <Paper 
            sx={{ 
              p: 2, 
              display: 'flex', 
              gap: 2,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              borderRadius: 3
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleAddRow}
              startIcon={<AddIcon />}
              sx={{ 
                fontWeight: 'bold',
                px: 3,
                '&:hover': { boxShadow: '0 6px 20px rgba(33, 150, 243, 0.3)' }
              }}
            >
              Add Row
            </Button>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleSaveChanges}
              startIcon={<SaveIcon />}
              sx={{ 
                fontWeight: 'bold',
                px: 3,
                '&:hover': { boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)' }
              }}
            >
              Save All Changes
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="large"
              onClick={() => setIsEditMode(false)}
              startIcon={<CloseIcon />}
              sx={{ 
                fontWeight: 'bold',
                px: 3,
                '&:hover': { backgroundColor: '#ffebee' }
              }}
            >
              Cancel
            </Button>
          </Paper>
        </Box>
      )}

      {/* Campaign Selector Modal for Snapshot Views */}
      <Dialog 
        open={showCampaignSelector} 
        onClose={() => setShowCampaignSelector(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarningIcon color="warning" />
            <Typography variant="h6">
              Campaign không tồn tại trong Snapshot tháng {snapshotMonth}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Campaign hiện tại không có trong snapshot. Vui lòng chọn một campaign khác từ danh sách có sẵn:
          </Typography>
          
          <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
            <List>
              {availableCampaigns.slice(0, 10).map((campaign) => (
                <ListItem 
                  key={campaign.id}
                  button
                  onClick={() => {
                    const newUrl = `/excel-view/${campaign.id}/${mediaType}?snapshotMonth=${snapshotMonth}`;
                    navigate(newUrl);
                    setShowCampaignSelector(false);
                  }}
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    mb: 1,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: '#f5f5f5' }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {campaign.campaign_name}
                        </Typography>
                        <Chip 
                          label={campaign.brand_name || 'No Brand'} 
                          size="small" 
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          ID: {campaign.id} | 
                          Budget: {formatCurrency(campaign.given_lc || 0)} | 
                          Status: {campaign.campaign_status}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {campaign.start_date} - {campaign.end_date}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {availableCampaigns.length > 10 && (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant="caption" color="text.secondary" align="center">
                        ... và {availableCampaigns.length - 10} campaigns khác
                      </Typography>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCampaignSelector(false)}>
            Đóng
          </Button>
          <Button 
            variant="contained"
            onClick={() => navigate('/campaigns')}
            startIcon={<ArrowBackIcon />}
          >
            Về trang Campaigns
          </Button>
        </DialogActions>
      </Dialog>

      {/* Data Menu */}
      <Menu
        anchorEl={dataMenuAnchor}
        open={Boolean(dataMenuAnchor)}
        onClose={handleDataMenuClose}
        PaperProps={{
          sx: {
            minWidth: 220,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2
          }
        }}
      >
        {selectedRowForMenu && (
          <>
            <MenuItem onClick={() => {
              handleLockToggle(selectedRowForMenu.id, selectedRowForMenu.is_locked);
              handleDataMenuClose();
            }}>
              <ListItemIcon>
                <Switch
                  checked={selectedRowForMenu.is_locked || false}
                  color={selectedRowForMenu.is_locked ? "error" : "success"}
                  size="small"
                />
              </ListItemIcon>
              <ListItemText 
                primary={selectedRowForMenu.is_locked ? "Request Unlock" : "Lock Row"}
                secondary={selectedRowForMenu.is_locked ? "Submit unlock request" : "Prevent editing"}
              />
            </MenuItem>
            
            <MenuItem onClick={() => {
              handleViewHistory(selectedRowForMenu.id);
              handleDataMenuClose();
            }}>
              <ListItemIcon>
                <HistoryIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText 
                primary="View History"
                secondary="See change history"
              />
            </MenuItem>

            {/* Show pending unlock requests indicator */}
            {rowUnlockStatus[selectedRowForMenu.id] === 'pending' && (
              <MenuItem disabled>
                <ListItemIcon>
                  <WarningIcon color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="Unlock Request Pending"
                  secondary="Waiting for admin approval"
                />
              </MenuItem>
            )}

            {/* Show unlock request status */}
            {selectedRowForMenu.is_locked && (
              <MenuItem 
                disabled 
                sx={{ 
                  backgroundColor: '#fff3e0',
                  '&.Mui-disabled': {
                    opacity: 0.8
                  }
                }}
              >
                <ListItemIcon>
                  <LockIcon sx={{ color: '#d32f2f' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Row is Locked"
                  secondary="Click 'Request Unlock' to submit unlock request"
                />
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      {/* History Modal */}
      <HistoryDialog
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        campaignId={campaignId}
        campaignName={campaign?.campaign_name}
      />

      {/* Unlock Request Dialog */}
      <Dialog 
        open={showUnlockDialog} 
        onClose={handleUnlockDialogCancel}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              backgroundColor: '#ff9800',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '1.2rem'
            }}>
              🔓
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Request Row Unlock
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Row ID: {unlockRowId} - Admin approval required
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Please provide a detailed reason for unlocking this row. Your request will be reviewed by an administrator.
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Reason for Unlock Request"
              placeholder="Examples:
• Need to correct data entry error
• Update required due to client request  
• Fix calculation mistake
• Other business justification"
              value={unlockReason}
              onChange={(e) => setUnlockReason(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
            />
            
            {/* File Upload Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                📎 Attach Supporting Document (Optional)
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Upload client email or document requesting the change (PDF only, max 10MB)
              </Typography>
              
              <Box 
                sx={{ 
                  border: '2px dashed #ccc', 
                  borderRadius: 2, 
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#ff9800',
                    backgroundColor: '#fff8e1'
                  }
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {uploadingFile ? (
                  // File uploading - show loading state
                  <Box>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Processing file...
                    </Typography>
                  </Box>
                ) : unlockAttachment ? (
                  // File uploaded - show file info
                  <Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      gap: 1,
                      mb: 2
                    }}>
                      <Box sx={{ fontSize: '2rem' }}>📄</Box>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {unlockAttachment.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {unlockAttachment.size > 1024 * 1024 
                            ? `${(unlockAttachment.size / 1024 / 1024).toFixed(2)} MB`
                            : `${(unlockAttachment.size / 1024).toFixed(0)} KB`
                          }
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleRemoveFile}
                        startIcon={<CloseIcon />}
                      >
                        Remove File
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => document.getElementById('unlock-attachment-input').click()}
                      >
                        Change File
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  // No file uploaded - show upload area
                  <Box>
                    <Box sx={{ fontSize: '3rem', mb: 2 }}>📁</Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Drag & drop a PDF file here, or click to select
                    </Typography>
                    <Button 
                      variant="outlined" 
                      onClick={() => document.getElementById('unlock-attachment-input').click()}
                      startIcon={<AttachFileIcon />}
                    >
                      Select PDF File
                    </Button>
                  </Box>
                )}
                
                {/* Hidden file input */}
                <input
                  id="unlock-attachment-input"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </Box>
              
              {/* Error message */}
              {attachmentError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  ⚠️ {attachmentError}
                </Typography>
              )}
              
              {/* Success message */}
              {unlockAttachment && !attachmentError && (
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  ✅ File uploaded successfully
                </Typography>
              )}
            </Box>
            
            <Box sx={{ 
              backgroundColor: '#fff3e0', 
              padding: 2, 
              borderRadius: 2,
              border: '1px solid #ffb74d'
            }}>
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 'bold', mb: 1 }}>
                ⚠️ Important Notes:
              </Typography>
              <Typography variant="body2" sx={{ color: '#bf360c' }}>
                • Unlock requests are tracked and audited
                • Admin will review your request, reason, and any attachments
                • Attach supporting documents (emails, client requests) to strengthen your case
                • You will be notified when the request is approved/rejected
                • Row will remain locked until approved
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleUnlockDialogCancel}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUnlockDialogSubmit}
            variant="contained"
            disabled={!unlockReason.trim() || uploadingFile}
            sx={{ 
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#f57c00' }
            }}
            startIcon={uploadingFile ? <CircularProgress size={16} color="inherit" /> : (unlockAttachment ? <AttachFileIcon /> : null)}
          >
            {uploadingFile ? 'Processing...' : `Submit Request${unlockAttachment ? ' + File' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
      </Container>
    </ExcelViewWrapper>
  );
};

export default ExcelLikeView; 